/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, ExpressCheckoutElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { API, publicApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Loader2, Lock, ShieldCheck, Truck, CheckCircle2, ChevronDown, ShoppingBag, RefreshCw, Headset, Star, Gift, Clock, HelpCircle, User, MapPin, CreditCard, Search } from 'lucide-react';
import { CHECKOUT } from '@/constants/testIds';
import { VivaPayForm } from '@/components/VivaPayForm';
import { PayzenPayForm } from '@/components/PayzenPayForm';
import { COUNTRIES } from '@/constants/countries';
import { toast } from 'sonner';
import { resolveImageUrl } from '@/components/ImageUpload';
import { useHideEmergentBadge } from '@/hooks/useHideEmergentBadge';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import PixelTracker from '@/components/PixelTracker';
import { trackEvent, captureUtm, setTrackVariant, getAttribution } from '@/lib/track';
import { getT, resolveLang, detectCountry, langFromCountry } from '@/lib/checkoutI18n';
import { getLumenT } from '@/lib/lumenI18n';
import { PromoCodeField, PromoSummaryRow, computePromoDiscount } from '@/components/checkout/PromoCodeField';
import { setActivePromo, getActivePromo } from '@/lib/promoState';
import { BrandCheckout } from '@/components/checkout/BrandCheckout';

const CURRENCY_SYMBOLS = { eur: '€', usd: '$', gbp: '£', pln: 'zł', sek: 'kr' };
// Active checkout currency symbol — assigned by the Checkout component during
// render so every nested component (PayForm, summaries, Lumen) formats prices
// with the store's Stripe-account currency. One checkout renders per page.
let __curSym = '€';
const money = (n) => `${Number(n || 0).toFixed(2)} ${__curSym}`;


// A/B test: pick a sticky 50/50 template variant for this visitor+store so the
// same person always sees the same variant (clean conversion attribution).
// The assignment key embeds the two variants, so changing the duel (a/b) in the
// admin automatically re-randomises existing visitors (fresh test).
const pickAbVariant = (storeId, a = 'classic', b = 'lumen') => {
  const valid = ['classic', 'lumen', 'shopify'];
  const va = valid.includes(a) ? a : 'classic';
  const vb = valid.includes(b) ? b : 'lumen';
  if (va === vb) return va; // not a real duel
  const key = `ec_abv_${storeId || 'x'}_${va}_${vb}`;
  try {
    const cur = localStorage.getItem(key);
    if (cur === va || cur === vb) return cur;
    const v = Math.random() < 0.5 ? va : vb;
    localStorage.setItem(key, v);
    return v;
  } catch {
    return Math.random() < 0.5 ? va : vb;
  }
};

// Maps a stored lucide icon key to its component (trust/footer items, Lumen template).
const LUMEN_ICON_MAP = {
  shield: ShieldCheck, lock: Lock, truck: Truck, refresh: RefreshCw,
  headset: Headset, check: CheckCircle2, star: Star, gift: Gift, clock: Clock,
};
const lumenIcon = (key) => LUMEN_ICON_MAP[key] || ShieldCheck;

// Cache loadStripe per publishable key so different stores can each have their own
// Stripe account without colliding.
const stripeCache = {};
const getStripe = (pk) => {
  if (!pk) return null;
  if (!stripeCache[pk]) stripeCache[pk] = loadStripe(pk);
  return stripeCache[pk];
};

// Convert any axios/network error into a human-readable string. Handles FastAPI's
// 422 validation error shape (`detail` is an array of {type, loc, msg, ...}).
const extractErrorMessage = (e, fallback = 'Une erreur est survenue') => {
  const detail = e?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0];
    const loc = Array.isArray(first?.loc) ? first.loc.slice(-2).join('.') : '';
    return loc ? `${first?.msg || 'Erreur de validation'} (${loc})` : (first?.msg || fallback);
  }
  return e?.message || fallback;
};

const buildAppearance = (accent) => ({
  theme: 'stripe',
  variables: {
    colorPrimary: accent || '#1A1A1A',
    colorBackground: '#FFFFFF',
    colorText: '#1A1A1A',
    colorDanger: '#DC2626',
    fontFamily: 'Inter, system-ui, sans-serif',
    fontSizeBase: '15px',
    borderRadius: '6px',
    spacingUnit: '4px',
  },
});

// LUMEN card form appearance — matches the Lumen spec (Inter, 48px inputs,
// 8px radius, neutral palette). Used only when the Lumen template is active.
const buildLumenAppearance = (accent) => ({
  theme: 'stripe',
  variables: {
    colorPrimary: accent || '#111111',
    colorBackground: '#FFFFFF',
    colorText: '#111111',
    colorTextSecondary: '#4B5563',
    colorDanger: '#DC2626',
    fontFamily: 'Inter, system-ui, sans-serif',
    fontSizeBase: '16px',
    borderRadius: '8px',
    spacingUnit: '4px',
  },
  rules: {
    '.Input': {
      border: '1px solid #E5E7EB',
      padding: '14px 16px',
      boxShadow: 'none',
    },
    '.Input:focus': {
      border: '1px solid #111111',
      boxShadow: '0 0 0 1px #111111',
    },
    '.Label': {
      color: '#4B5563',
      fontSize: '12px',
      fontWeight: '500',
    },
    '.Tab, .Block': {
      border: '1px solid #E5E7EB',
      borderRadius: '12px',
    },
  },
});

// SHOPIFY-style card form appearance — mimics Shopify's native checkout payment
// fields (light gray field bg, 5px radius, subtle border). Used when the
// 'shopify' template is active.
const buildBrandAppearance = (accent, fontFamily) => ({
  theme: 'stripe',
  variables: {
    colorPrimary: accent || '#111111', colorText: '#1a1a1a', colorTextSecondary: '#52525b', colorDanger: '#dc2626',
    fontFamily: fontFamily ? `"${fontFamily}", system-ui, sans-serif` : 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
    borderRadius: '8px', spacingUnit: '4px', fontSizeBase: '16px',
  },
  rules: {
    '.Input': { border: '1px solid #d4d4d8', boxShadow: 'none', padding: '14px 12px' },
    '.Input:focus': { border: `1px solid ${accent || '#111111'}`, boxShadow: `0 0 0 1px ${accent || '#111111'}` },
    '.Label': { fontSize: '15px', fontWeight: '500', color: '#27272a' },
    '.Tab': { border: '1px solid #d4d4d8', borderRadius: '8px' },
    '.Tab--selected': { border: `2px solid ${accent || '#111111'}`, boxShadow: 'none', color: accent || '#111111' },
  },
});

const buildShopifyAppearance = (accent) => ({
  theme: 'stripe',
  variables: {
    colorPrimary: accent || '#1A1A1A',
    colorBackground: '#FFFFFF',
    colorText: '#1A1A1A',
    colorTextSecondary: '#6B7280',
    colorDanger: '#D82C0D',
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontSizeBase: '15px',
    borderRadius: '5px',
    spacingUnit: '4px',
  },
  rules: {
    '.Input': {
      border: '1px solid #8A8A8A',
      padding: '13px 12px',
      boxShadow: 'none',
    },
    '.Input:focus': {
      border: '2px solid #1A73E8',
      boxShadow: 'none',
    },
    '.Label': {
      color: '#6B7280',
      fontSize: '13px',
    },
    '.Tab, .Block': {
      border: '1px solid #D9D9D9',
      borderRadius: '8px',
    },
  },
});


export default function Checkout() {
  const params = useParams();
  // Detect multi-product cart mode via ?cart=<base64-of-[{shopify_id|vitrine_id, qty}]>
  const search = new URLSearchParams(window.location.search);
  const cartParam = search.get('cart') || '';
  const storeParam = search.get('store') || '';
  const isCartMode = !!cartParam;
  const source = params.vitrineId ? 'vitrine' : 'shopify';
  const lookupPath = params.vitrineId
    ? `/public/products/by-vitrine/${params.vitrineId}`
    : params.shopifyId
    ? `/public/products/by-shopify/${params.shopifyId}`
    : params.productId
    ? `/public/products/${params.productId}?source=${source}`
    : '';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  useHideEmergentBadge();
  const [cartLines, setCartLines] = useState([]); // [{product, quantity}]
  const [skipped, setSkipped] = useState([]);     // products in cart param but not mapped
  const [shippingOptions, setShippingOptions] = useState([]);
  const [selectedShippingId, setSelectedShippingId] = useState('');
  const [stripePk, setStripePk] = useState('');
  const [psp, setPsp] = useState('stripe');
  const [vivaCfg, setVivaCfg] = useState(null);
  const [payzenCfg, setPayzenCfg] = useState(null);
  const [promo, setPromo] = useState(null);
  const [platform, setPlatform] = useState('shopify');
  const [branding, setBranding] = useState({ display_name: '', logo_url: '', accent_color: '', support_email: '' });
  const [currency, setCurrency] = useState('eur');
  const [saveCard, setSaveCard] = useState(true);
  const [paymentMode, setPaymentMode] = useState('element'); // 'element' | 'invoice' (Pay by Stripe)
  __curSym = CURRENCY_SYMBOLS[currency] || currency.toUpperCase();
  useDocumentTitle(branding.display_name ? `Checkout — ${branding.display_name}` : 'Checkout');
  const [pixels, setPixels] = useState({ store_id: '', store_slug: '', meta_pixel_id: '', tiktok_pixel_id: '', google_tag_id: '', google_conversion_label: '' });
  const [form, setForm] = useState(() => ({
    email: '', first_name: '', last_name: '', address1: '',
    city: '', postal_code: '',
    country: detectCountry(COUNTRIES.map((c) => c.code)) || 'FR',
    phone: '',
  }));
  const localeCountry = useRef(null);
  const [geoLang, setGeoLang] = useState('');

  // Guess the customer's country from their IP and pre-select it to speed up
  // address entry. Only overrides the locale-default while the buyer hasn't
  // manually changed the country yet. Also derives an IP-based language used
  // as a fallback when the store has no fixed language. Fails silently.
  useEffect(() => {
    if (localeCountry.current === null) localeCountry.current = form.country;
    let alive = true;
    publicApi.get(`${API}/public/geo`).then((r) => {
      if (!alive) return;
      const cc = (r.data?.country || '').toUpperCase();
      setGeoLang(langFromCountry(cc));
      if (!cc || !COUNTRIES.some((c) => c.code === cc)) return;
      setForm((p) => (p.country === localeCountry.current ? { ...p, country: cc } : p));
    }).catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // One-shot: load product(s) + branding/config (NO PaymentIntent here —
  // the PI is now created lazily once the customer has filled the required
  // fields AND picked a shipping option, see the "deferred PI" useEffect below).
  useEffect(() => {
    // Persist UTM params from the entry URL so every event in this session
    // carries the right attribution, even if the URL is later normalized.
    captureUtm();
    const abortController = new AbortController();
    let mounted = true;
    (async () => {
      try {
        let lines = [];
        if (isCartMode) {
          // Multi-product cart: decode and resolve via the backend
          let rawItems;
          try {
            rawItems = JSON.parse(decodeURIComponent(escape(atob(cartParam))));
          } catch {
            throw new Error('Lien de panier invalide');
          }
          const navLang = (navigator.language || '').slice(0, 2).toLowerCase();
          const res = await publicApi.post(`${API}/public/cart/resolve`, { items: rawItems, store_id: storeParam || '', lang: navLang }, { signal: abortController.signal });
          if (!res.data.items.length) {
            throw new Error('Aucun produit disponible dans ce panier');
          }
          lines = res.data.items.map((l) => ({ product: l.product, quantity: l.quantity, variant: l.variant || null }));
          if (res.data.skipped && res.data.skipped.length) {
            if (mounted) setSkipped(res.data.skipped);
            toast.warning(`${res.data.skipped.length} article(s) du panier non disponible(s)`);
          }
        } else {
          // Single product (legacy URLs)
          const pr = await publicApi.get(`${API}${lookupPath}`, { signal: abortController.signal });
          lines = [{ product: pr.data.product, quantity: 1 }];
        }
        if (!mounted) return;
        setCartLines(lines);

        // Per-store branding + Stripe pk + shipping options + pixels (single round-trip if possible)
        const firstId = lines[0].product.id;
        const [cfg, pxRes] = await Promise.all([
          publicApi.get(`${API}/public/checkout/config/${firstId}`, { signal: abortController.signal }),
          publicApi.get(`${API}/public/checkout/pixels/${firstId}`, { signal: abortController.signal }),
        ]);
        if (!mounted) return;
        setStripePk(cfg.data.stripe_public_key);
        setPsp(cfg.data.psp || 'stripe');
        setCurrency((cfg.data.currency || 'eur').toLowerCase());
        setSaveCard(cfg.data.save_card !== false);
        setPaymentMode(cfg.data.payment_mode || 'element');
        setVivaCfg(cfg.data.viva || null);
        setPayzenCfg(cfg.data.payzen || null);
        setPlatform(cfg.data.platform || 'shopify');
        setBranding(cfg.data.branding || {});
        // Merchant-configured promo applied automatically (no manual entry needed).
        if (cfg.data.auto_promo) setPromo(cfg.data.auto_promo);
        // Shared-domain white-label: reflect the merchant's brand in the browser tab.
        // Prefer a dedicated favicon, fall back to the store logo.
        const favSrc = resolveImageUrl(cfg.data.branding?.checkout_ui?.favicon_url || cfg.data.branding?.logo_url);
        if (favSrc) {
          let link = document.querySelector("link[rel~='icon']");
          if (!link) {
            link = document.createElement('link');
            link.rel = 'icon';
            document.head.appendChild(link);
          }
          link.href = favSrc;
        }
        const opts = cfg.data.shipping_options || [];
        setShippingOptions(opts);
        setPixels(pxRes.data || {});
        // Auto-preselect the DEFAULT shipping option (or the first) to remove a
        // friction step on mobile — the customer can still switch.
        const def = opts.find((o) => o.is_default) || opts[0];
        if (def) setSelectedShippingId(def.id);
        // The Stripe PaymentIntent is NOT created here anymore — it's deferred
        // until the customer has filled their info AND picked shipping. This
        // dramatically reduces the volume of "abandoned" PIs Stripe sees and
        // keeps the metadata they receive accurate (no placeholder customer).
      } catch (e) {
        // Ignore axios cancellations from StrictMode double-invoke unmount
        if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED' || axios.isCancel?.(e)) return;
        if (mounted) setError(extractErrorMessage(e, 'Produit introuvable'));
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
      abortController.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateField = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const [summaryOpen, setSummaryOpen] = useState(false);

  // Minimum info required before we let the customer actually pay.
  const hasRequiredCustomerInfo = !!(
    form.email && form.first_name && form.last_name &&
    form.address1 && form.city && form.postal_code && form.country
  );

  const setQuantity = (newQty) => {
    // Only allowed in single-product mode
    if (isCartMode || cartLines.length !== 1) return;
    setCartLines([{ ...cartLines[0], quantity: Math.max(1, newQty) }]);
  };
  const quantity = cartLines.length === 1 ? cartLines[0].quantity : 1;
  const product = cartLines[0]?.product || null; // backwards-compat reference for the header
  const selectedShipping = shippingOptions.find((o) => o.id === selectedShippingId) || null;
  // "2nd article" offer. Mirrors the backend compute_bogo():
  //  - scope same_product: per line, floor(qty/2) units rewarded.
  //  - scope cheapest_in_cart: pool eligible units, cheapest floor(total/2) rewarded.
  // Each rewarded unit is discounted by its product's percent (100% if free).
  // The backend recomputes authoritatively for the Stripe charge.
  const unitPct = (p) => (p.bogo_offer_type === 'second_percent'
    ? Math.max(0, Math.min(100, p.bogo_discount_percent || 0))
    : 100);
  const lineGross = (line) => line.product.price * line.quantity;
  const computeBogo = (lines) => {
    const disc = lines.map(() => 0);
    const pool = [];
    const groups = {}; // product id -> [lineIndex] for same_product aggregation
    lines.forEach((l, idx) => {
      const p = l.product;
      if (!p.bogo_enabled) return;
      if (p.bogo_scope === 'cheapest_in_cart') {
        const pct = unitPct(p);
        for (let i = 0; i < l.quantity; i += 1) pool.push({ price: p.price, idx, pct });
      } else {
        (groups[p.id] = groups[p.id] || []).push(idx);
      }
    });
    // same_product: aggregate units of the same product across its (variant) lines.
    Object.values(groups).forEach((idxs) => {
      const totalQty = idxs.reduce((s, i) => s + lines[i].quantity, 0);
      let freeRemaining = Math.floor(totalQty / 2);
      idxs.forEach((i) => {
        if (freeRemaining <= 0) return;
        const p = lines[i].product;
        const unitsFree = Math.min(lines[i].quantity, freeRemaining);
        disc[i] += unitsFree * p.price * unitPct(p) / 100;
        freeRemaining -= unitsFree;
      });
    });
    if (pool.length) {
      const rewarded = Math.floor(pool.length / 2);
      pool.sort((a, b) => a.price - b.price);
      pool.slice(0, rewarded).forEach((u) => { disc[u.idx] += u.price * u.pct / 100; });
    }
    return disc.map((d) => Math.round(d * 100) / 100);
  };
  const lineDiscounts = computeBogo(cartLines);
  const t = getT(resolveLang(branding.language, geoLang));

  // Admin live-preview overrides (passed by the CheckoutDesign iframe):
  //  ?pv=1        → preview mode (no tracking / no pixels)
  //  ?pv_tpl=...  → force a specific template so the merchant sees it live
  const { previewTpl, isPreview } = useMemo(() => {
    const sp = new URLSearchParams(window.location.search);
    return { previewTpl: sp.get('pv_tpl') || '', isPreview: sp.get('pv') === '1' };
  }, []);

  // Resolve which checkout template to show. With A/B test enabled, each
  // visitor gets a sticky 50/50 variant; otherwise the store's chosen template.
  const activeVariant = useMemo(() => {
    if (['classic', 'lumen', 'shopify', 'brand'].includes(previewTpl)) return previewTpl;
    const ui = branding.checkout_ui || {};
    const tpl = ui.template || 'classic';
    if (ui.ab_test_enabled) {
      const va = ui.ab_variant_a || 'classic';
      const vb = ui.ab_variant_b || (tpl !== 'classic' ? tpl : 'lumen');
      return pickAbVariant(pixels.store_id || params.productId || 'x', va, vb);
    }
    return tpl;
  }, [previewTpl, branding.checkout_ui, pixels.store_id, params.productId]);

  // Fire one `checkout_view` event (A/B denominator) and tag every later event
  // of this page load with the variant. Skipped in admin preview mode.
  const viewFiredRef = useRef(false);
  useEffect(() => {
    if (loading || !pixels.store_id || viewFiredRef.current || isPreview) return;
    viewFiredRef.current = true;
    setTrackVariant(activeVariant);
    trackEvent('checkout_view', { storeId: pixels.store_id, slug: pixels.store_slug, source, variant: activeVariant });
  }, [loading, pixels.store_id, pixels.store_slug, activeVariant, source, isPreview]);

  const offerTag = (p) => (p.bogo_offer_type === 'second_percent'
    ? t.offer_percent.replace('{pct}', Math.round(p.bogo_discount_percent || 0))
    : t.offer_free);
  const hasFreeOffer = cartLines.some((l) => l.product.bogo_enabled && l.product.bogo_offer_type !== 'second_percent');
  const hasPercentOffer = cartLines.some((l) => l.product.bogo_enabled && l.product.bogo_offer_type === 'second_percent');
  const summaryOfferLabel = hasPercentOffer && !hasFreeOffer
    ? t.bogo_percent_summary
    : t.bogo_free;
  const subtotal = cartLines.reduce((s, l) => s + lineGross(l), 0);
  const bogoDiscount = lineDiscounts.reduce((s, d) => s + d, 0);
  const promoDiscount = computePromoDiscount(promo, subtotal - bogoDiscount);
  const total = subtotal - bogoDiscount - promoDiscount + (selectedShipping?.price || 0);
  const promoCode = promoDiscount > 0 ? promo.code : '';
  useEffect(() => { setActivePromo(promoCode); }, [promoCode]);
  const ckLang = resolveLang(branding.language, geoLang);
  const promoProps = {
    productId: cartLines[0]?.product?.id, amount: subtotal - bogoDiscount, email: form.email,
    promo, setPromo, lang: ckLang, accent: branding.accent_color, money,
  };

  // Memoize Elements options so the object identity is stable across renders
  // (rebuilding it inline would trigger react-stripe-js 'Unsupported prop change'
  // and remount the PaymentElement, wiping user-typed card data).
  // Stripe deferred-intent mode: the Payment Element renders BEFORE any
  // PaymentIntent exists, so the card form is visible from the start. The amount
  // is the initial cart total (subtotal − BOGO); shipping is added later and the
  // amount is kept in sync via elements.update({ amount }) inside PayForm. The
  // PaymentIntent itself is created server-side only at pay time (after a
  // shipping option is chosen — the Pay button stays disabled until then).
  // We EXCLUDE heavy wallets (Amazon Pay, PayPal) whose third-party SDKs spawn
  // extra iframes and blow the iOS/WebKit memory budget — a root cause of the
  // checkout crashing & reloading on iPhone. We use `excludedPaymentMethodTypes`
  // (NOT `paymentMethodTypes`): in deferred mode this stays compatible with the
  // backend's automatic_payment_methods (no mismatch error) and works across
  // multi-tenant accounts regardless of which methods they activated. Link is
  // hidden via the PaymentElement `wallets.link:'never'`. Keep the excluded list
  // in sync with routes/checkout.py.
  const brandKit = (branding.checkout_ui && branding.checkout_ui.brand) || {};
  const elementsOptions = useMemo(
    () => (stripePk ? {
      mode: 'payment',
      amount: Math.max(50, Math.round((subtotal - bogoDiscount - promoDiscount) * 100)),
      currency: currency,
      excludedPaymentMethodTypes: ['amazon_pay', 'paypal'],
      // Save the CARD off-session (→ Clients directory / BatCycle billable). Scoped
      // to `card` only so Klarna/iDEAL/Bancontact are untouched. MUST stay in sync
      // with the backend PaymentIntent `payment_method_options.card.setup_future_usage`
      // (routes/checkout.py) — Stripe's deferred flow rejects confirmPayment on any
      // mismatch between the Elements config and the PaymentIntent. Only set when the
      // Stripe account has token capture enabled (config.save_card); otherwise omit
      // so the backend (which also omits it) and the frontend stay in sync.
      ...(saveCard ? { paymentMethodOptions: { card: { setup_future_usage: 'off_session' } } } : {}),
      ...(activeVariant === 'brand' && brandKit.font_url ? { fonts: [{ cssSrc: brandKit.font_url }] } : {}),
      appearance: activeVariant === 'lumen'
        ? buildLumenAppearance(branding.accent_color)
        : activeVariant === 'brand'
          ? buildBrandAppearance(branding.accent_color, brandKit.font_family)
        : activeVariant === 'shopify'
          ? buildShopifyAppearance(branding.accent_color)
          : buildAppearance(branding.accent_color),
    } : null),
    // amount intentionally excluded: react-stripe-js forbids changing it via
    // props after mount — PayForm calls elements.update({ amount }) instead.
    [stripePk, branding.accent_color, activeVariant, currency, saveCard, brandKit.font_url, brandKit.font_family] // eslint-disable-line react-hooks/exhaustive-deps
  );

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-zinc-400" /></div>;
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Card className="p-10 max-w-md w-full text-center">
          <h1 className="font-display text-2xl font-bold mb-2">{t.product_not_found}</h1>
          <p className="text-zinc-600" data-testid={CHECKOUT.errorMessage}>{error}</p>
        </Card>
      </div>
    );
  }

  const stripeInstance = getStripe(stripePk);

  const stripeItems = cartLines.map((l) => ({
    product_id: l.product.id,
    quantity: l.quantity,
    variant_id: l.variant?.id || null,
    variant_title: l.variant?.title || null,
    variant_image: l.variant?.image || null,
  }));

  const ui = branding.checkout_ui || {};

  // BRAND template — built from the store's own brand kit (logo/colours/font).
  if (activeVariant === 'brand') {
    return (
      <BrandCheckout
        vm={{
          branding, t, pixels, cartLines, lineDiscounts, subtotal, bogoDiscount,
          summaryOfferLabel, selectedShipping, total, offerTag, skipped, isCartMode,
          quantity, setQuantity, psp, vivaCfg, payzenCfg, platform, stripeInstance, elementsOptions,
          form, updateField, setForm, hasRequiredCustomerInfo, shippingOptions,
          selectedShippingId, setSelectedShippingId, source, stripeItems, isPreview, geoLang,
          paymentMode, currency, promo, promoDiscount, promoProps,
        }}
        parts={{ CustomerFields, ShippingPicker, PayForm, ExpressCheckout, StripeInvoicePay, TrustpilotBlock, money, resolveLang }}
      />
    );
  }

  // SHOPIFY-style alternative checkout template (trust-optimized, 2 columns).
  if (activeVariant === 'shopify') {
    return (
      <ShopifyCheckout vm={{
        branding, t, pixels, cartLines, lineDiscounts, subtotal, bogoDiscount,
        summaryOfferLabel, selectedShipping, total, offerTag, skipped, isCartMode,
        quantity, setQuantity, psp, vivaCfg, payzenCfg, platform, stripeInstance, elementsOptions,
        form, updateField, setForm, hasRequiredCustomerInfo, shippingOptions,
        selectedShippingId, setSelectedShippingId, source, summaryOpen, setSummaryOpen,
        stripeItems, isPreview, geoLang, paymentMode, currency, promo, promoDiscount, promoProps,
      }} />
    );
  }

  // LUMEN alternative checkout template (selectable per store / via A/B test).
  if (activeVariant === 'lumen') {
    return (
      <LumenCheckout vm={{
        branding, t, pixels, cartLines, lineDiscounts, subtotal, bogoDiscount,
        summaryOfferLabel, selectedShipping, total, offerTag, skipped, isCartMode,
        quantity, setQuantity, psp, vivaCfg, payzenCfg, platform, stripeInstance, elementsOptions,
        form, updateField, setForm, hasRequiredCustomerInfo, shippingOptions,
        selectedShippingId, setSelectedShippingId, source, summaryOpen, setSummaryOpen,
        stripeItems, isPreview, geoLang, paymentMode, currency, promo, promoDiscount, promoProps,
      }} />
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {pixels.store_id && !isPreview && (
        <PixelTracker
          storeId={pixels.store_id}
          slug={pixels.store_slug}
          event="page_view_checkout"
          metaPixelId={pixels.meta_pixel_id}
          tiktokPixelId={pixels.tiktok_pixel_id}
          googleTagId={pixels.google_tag_id}
          googleLabel={pixels.google_conversion_label}
        />
      )}
      {/* Top trust bar (uses store accent color when defined) */}
      <div className="text-white text-xs" style={{ backgroundColor: branding.accent_color || '#0A0A0A' }}>
        <div className="max-w-6xl mx-auto px-6 py-2 flex items-center justify-center gap-6">
          <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> {t.trust_secure}</span>
          <span className="hidden sm:inline-flex items-center gap-1.5"><Truck className="h-3.5 w-3.5" /> {t.trust_fast_ship}</span>
          <span className="hidden sm:inline-flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5" /> {t.trust_money_back}</span>
        </div>
      </div>

      {/* MOBILE: collapsible order summary (Shopify-style) — total always visible */}
      <MobileSummaryBar
        open={summaryOpen}
        onToggle={() => setSummaryOpen((v) => !v)}
        cartLines={cartLines}
        lineDiscounts={lineDiscounts}
        subtotal={subtotal}
        bogoDiscount={bogoDiscount}
        summaryOfferLabel={summaryOfferLabel}
        selectedShipping={selectedShipping}
        total={total}
        accentColor={branding.accent_color}
        t={t}
        promoProps={promoProps}
        promoDiscount={promoDiscount}
      />

      <div className="grid lg:grid-cols-[1fr_460px] min-h-screen">
        {/* LEFT: customer form + payment */}
        <div className="px-4 sm:px-6 lg:px-16 py-6 lg:py-12 pb-32 lg:pb-12 order-2 lg:order-1">
          <div className="max-w-xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3 min-w-0" data-testid="checkout-store-brand">
                {branding.logo_url ? (
                  <img
                    src={resolveImageUrl(branding.logo_url)}
                    alt={branding.display_name}
                    className="h-9 max-w-[180px] object-contain"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                ) : (
                  <>
                    <div
                      className="w-9 h-9 flex items-center justify-center rounded-md"
                      style={{ backgroundColor: branding.accent_color || '#1A1A1A' }}
                    >
                      <Lock className="h-4 w-4 text-white" />
                    </div>
                    <span className="font-display font-bold tracking-tight text-lg truncate">
                      {branding.display_name || t.store_fallback}
                    </span>
                  </>
                )}
              </div>
              <div className="text-xs text-zinc-500 flex items-center gap-2 shrink-0">
                <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" /> SSL · PCI DSS
              </div>
            </div>

            <h1 className="font-display text-2xl lg:text-3xl font-bold tracking-tight mb-1">{t.finalize_order}</h1>
            <p className="text-sm text-zinc-500 mb-6">{t.one_step}</p>

            {/* EXPRESS CHECKOUT (Apple Pay / Google Pay) — top of form, Shopify-style.
                Lives in its OWN Elements instance so elements.submit() never
                validates the card PaymentElement below. Renders nothing when no
                wallet is available on the device. */}
            {psp === 'stripe' && paymentMode === 'element' && stripeInstance && elementsOptions && (
              <Elements stripe={stripeInstance} options={elementsOptions}>
                <ExpressCheckout
                  amountCents={Math.max(50, Math.round(total * 100))}
                  total={total}
                  items={cartLines.map((l) => ({
                    product_id: l.product.id,
                    quantity: l.quantity,
                    variant_id: l.variant?.id || null,
                    variant_title: l.variant?.title || null,
                    variant_image: l.variant?.image || null,
                  }))}
                  source={source}
                  selectedShipping={selectedShipping}
                  shippingOptionId={selectedShippingId}
                  accentColor={branding.accent_color}
                  storeId={pixels.store_id}
                  storeSlug={pixels.store_slug}
                  t={t}
                />
              </Elements>
            )}

            <section className="mb-8">
              <h2 className="text-sm font-semibold mb-4 flex items-center gap-2">
                <span
                  className="w-6 h-6 text-white rounded-full text-xs font-bold flex items-center justify-center"
                  style={{ backgroundColor: branding.accent_color || '#1A1A1A' }}
                >1</span>
                {t.your_details}
              </h2>
              <CustomerFields form={form} updateField={updateField} setForm={setForm} t={t} />
            </section>

            <section className="mb-8">
              <h2 className="text-sm font-semibold mb-4 flex items-center gap-2">
                <span
                  className="w-6 h-6 text-white rounded-full text-xs font-bold flex items-center justify-center"
                  style={{ backgroundColor: branding.accent_color || '#1A1A1A' }}
                >2</span>
                {t.shipping}
              </h2>
              <ShippingPicker
                options={shippingOptions}
                value={selectedShippingId}
                onChange={setSelectedShippingId}
                accentColor={branding.accent_color}
                t={t}
              />
            </section>

            <section>
              <h2 className="text-sm font-semibold mb-4 flex items-center gap-2">
                <span
                  className="w-6 h-6 text-white rounded-full text-xs font-bold flex items-center justify-center"
                  style={{ backgroundColor: branding.accent_color || '#1A1A1A' }}
                >3</span>
                {t.payment}
              </h2>
              {psp === 'payzen' ? (
                <PayzenPayForm
                  items={cartLines.map((l) => ({ product_id: l.product.id, quantity: l.quantity, variant_id: l.variant?.id || null, variant_title: l.variant?.title || null, variant_image: l.variant?.image || null }))}
                  total={total} form={form} hasRequiredCustomerInfo={hasRequiredCustomerInfo} source={source}
                  shippingOptionId={selectedShippingId} shippingChosen={!!selectedShipping}
                  accentColor={branding.accent_color} storeId={pixels.store_id} storeSlug={pixels.store_slug}
                  currency={currency} payzenCfg={payzenCfg} t={t}
                />
              ) : psp === 'viva' ? (
                vivaCfg ? (
                  <VivaPayForm
                    items={cartLines.map((l) => ({
                      product_id: l.product.id,
                      quantity: l.quantity,
                      variant_id: l.variant?.id || null,
                      variant_title: l.variant?.title || null,
                      variant_image: l.variant?.image || null,
                    }))}
                    total={total}
                    form={form}
                    hasRequiredCustomerInfo={hasRequiredCustomerInfo}
                    source={source}
                    shippingOptionId={selectedShippingId}
                    shippingChosen={!!selectedShipping}
                    accentColor={branding.accent_color}
                    storeId={pixels.store_id}
                    storeSlug={pixels.store_slug}
                    vivaCfg={vivaCfg}
                    firstProductId={cartLines[0]?.product?.id}
                    currency="eur"
                    embedded={false}
                    t={t}
                  />
                ) : (
                  <div className="h-32 flex flex-col items-center justify-center text-zinc-400 gap-2" data-testid="checkout-pay-loading-viva">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span className="text-xs">{t.loading_payment}</span>
                  </div>
                )
              ) : (paymentMode === 'invoice' || paymentMode === 'link') ? (
                <StripeInvoicePay
                  mode={paymentMode}
                  total={total}
                  items={cartLines.map((l) => ({
                    product_id: l.product.id,
                    quantity: l.quantity,
                    variant_id: l.variant?.id || null,
                    variant_title: l.variant?.title || null,
                    variant_image: l.variant?.image || null,
                  }))}
                  form={form}
                  hasRequiredCustomerInfo={hasRequiredCustomerInfo}
                  source={source}
                  shippingOptionId={selectedShippingId}
                  shippingChosen={!!selectedShipping}
                  accentColor={branding.accent_color}
                  storeId={pixels.store_id}
                  storeSlug={pixels.store_slug}
                  t={t}
                />
              ) : stripeInstance && elementsOptions ? (
                <Elements stripe={stripeInstance} options={elementsOptions}>
                  <PayForm
                    total={total}
                    amountCents={Math.max(50, Math.round(total * 100))}
                    items={cartLines.map((l) => ({
                      product_id: l.product.id,
                      quantity: l.quantity,
                      variant_id: l.variant?.id || null,
                      variant_title: l.variant?.title || null,
                      variant_image: l.variant?.image || null,
                    }))}
                    form={form}
                    hasRequiredCustomerInfo={hasRequiredCustomerInfo}
                    source={source}
                    shippingOptionId={selectedShippingId}
                    shippingChosen={!!selectedShipping}
                    accentColor={branding.accent_color}
                    storeId={pixels.store_id}
                    storeSlug={pixels.store_slug}
                    t={t}
                  />
                </Elements>
              ) : (
                <div className="h-32 flex flex-col items-center justify-center text-zinc-400 gap-2" data-testid="checkout-pay-loading">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span className="text-xs">{t.loading_payment}</span>
                </div>
              )}
            </section>

            <div className="mt-8 flex items-center justify-center gap-4 text-[10px] text-zinc-400 font-mono uppercase tracking-wider">
              <span>VISA</span><span>·</span><span>MASTERCARD</span><span>·</span><span>AMEX</span><span>·</span><span>APPLE PAY</span><span>·</span><span>GOOGLE PAY</span>
            </div>

            {/* Trustpilot — mobile only (desktop shows it in the right column) */}
            <TrustpilotBlock ui={ui} className="lg:hidden mt-6" testId="trustpilot-block-mobile" />
          </div>
        </div>

        {/* RIGHT: order summary — desktop only (mobile uses the collapsible bar above) */}
        <div className="hidden lg:block bg-zinc-50 px-4 sm:px-6 lg:px-12 py-6 lg:py-12 border-b lg:border-b-0 lg:border-l border-zinc-200 lg:sticky lg:top-0 lg:h-screen order-1 lg:order-2 lg:overflow-y-auto">
          <div className="max-w-md mx-auto">
            <div className="overline mb-4">— {t.summary}</div>
            <div className="space-y-3" data-testid={CHECKOUT.productCard}>
              {cartLines.map((line, idx) => {
                // Prefer the variant image when present (each colour may have its
                // own photo); fall back to the base product image.
                const displayImage = line.variant?.image || line.product.image_url;
                const variantLabel = line.variant?.title || '';
                const lineDisc = lineDiscounts[idx] || 0;
                const lineNet = lineGross(line) - lineDisc;
                return (
                <Card key={`${line.product.id}-${idx}`} className="bg-white border-zinc-200 p-4" data-testid={`checkout-cart-line-${idx}`}>
                  <div className="flex gap-3">
                    {displayImage ? (
                      <img src={displayImage} alt={line.product.name} className="w-16 h-16 object-cover rounded-md border border-zinc-100" data-testid={`checkout-cart-line-${idx}-image`} />
                    ) : <div className="w-16 h-16 bg-zinc-100 rounded-md shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm leading-tight truncate">{line.product.name}</div>
                      {line.product.bogo_enabled && lineDisc > 0 && (
                        <div className="inline-flex items-center gap-1 mt-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5" data-testid={`checkout-cart-line-${idx}-bogo`}>
                          {offerTag(line.product)}
                        </div>
                      )}
                      {variantLabel && (
                        <div className="text-xs text-zinc-600 mt-0.5 truncate" data-testid={`checkout-cart-line-${idx}-variant`}>
                          {variantLabel}
                        </div>
                      )}
                      {line.product.sku && <div className="text-[10px] text-zinc-400 mt-0.5 font-mono">{line.product.sku}</div>}
                      {!isCartMode && cartLines.length === 1 ? (
                        <div className="mt-2 flex items-center gap-1.5">
                          <button type="button" data-testid="checkout-qty-minus" onClick={() => setQuantity(quantity - 1)} className="w-7 h-7 rounded-md border border-zinc-200 hover:bg-zinc-50">−</button>
                          <Input
                            data-testid={CHECKOUT.quantityInput}
                            type="number" value={quantity} min={1}
                            onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                            className="w-12 h-7 text-center text-sm rounded-md"
                          />
                          <button type="button" data-testid="checkout-qty-plus" onClick={() => setQuantity(quantity + 1)} className="w-7 h-7 rounded-md border border-zinc-200 hover:bg-zinc-50">+</button>
                        </div>
                      ) : (
                        <div className="mt-1.5 text-xs text-zinc-500">{t.qty} : <span className="font-mono">{line.quantity}</span></div>
                      )}
                    </div>
                    <div className="text-right text-sm font-semibold shrink-0">
                      {lineDisc > 0 ? (
                        <div data-testid={`checkout-cart-line-${idx}-price`}>
                          <span className="line-through text-zinc-400 font-normal text-xs mr-1">{money(lineGross(line))}</span>
                          <span className="text-emerald-700">{money(lineNet)}</span>
                        </div>
                      ) : (
                        <span data-testid={`checkout-cart-line-${idx}-price`}>{money(lineGross(line))}</span>
                      )}
                    </div>
                  </div>
                </Card>
                );
              })}
              {skipped.length > 0 && (
                <div data-testid="checkout-skipped-warning" className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">
                  ⚠️ {skipped.length > 1 ? t.skipped_many.replace('{n}', skipped.length) : t.skipped_one}
                </div>
              )}
            </div>

            <div className="mt-5"><PromoCodeField {...promoProps} /></div>
            <div className="mt-5 space-y-2.5 text-sm">
              <div className="flex justify-between">
                <span className="text-zinc-600">{t.subtotal}</span>
                <span className="font-mono">{money(subtotal)}</span>
              </div>
              {bogoDiscount > 0 && (
                <div className="flex justify-between text-emerald-700" data-testid="checkout-bogo-discount">
                  <span className="flex items-center gap-1.5">{summaryOfferLabel}</span>
                  <span className="font-mono">−{money(bogoDiscount)}</span>
                </div>
              )}
              <PromoSummaryRow promo={promo} discount={promoDiscount} lang={ckLang} money={money} />
              <div className="flex justify-between">
                <span className="text-zinc-600 flex items-center gap-1.5">
                  <Truck className="h-3.5 w-3.5" /> {selectedShipping?.name || t.shipping}
                </span>
                <span className="font-mono">
                  {selectedShipping ? money(selectedShipping.price) : t.to_choose}
                </span>
              </div>
              <div className="border-t border-zinc-200 pt-3 flex justify-between items-baseline">
                <span className="text-lg font-semibold">{t.total}</span>
                <span data-testid={CHECKOUT.totalAmount} className="text-2xl font-bold font-mono">{money(total)}</span>
              </div>
            </div>

            <div className="mt-8 space-y-3 text-xs text-zinc-500">
              <div className="flex items-start gap-2"><ShieldCheck className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" /><span>{t.note_encrypted}</span></div>
              <div className="flex items-start gap-2"><Truck className="h-4 w-4 text-zinc-700 mt-0.5 shrink-0" /><span>{t.note_ship_after}</span></div>
              <div className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-zinc-700 mt-0.5 shrink-0" /><span>{t.note_money_back}</span></div>
              {branding.support_email && (
                <div className="flex items-start gap-2 pt-2 border-t border-zinc-100">
                  <span className="text-zinc-400">{t.need_help}</span>
                  <a href={`mailto:${branding.support_email}`} className="text-zinc-700 hover:underline" data-testid="checkout-support-email">
                    {branding.support_email}
                  </a>
                </div>
              )}
            </div>

            <TrustpilotBlock ui={ui} className="mt-8" />
          </div>
        </div>
      </div>
    </div>
  );
}

const CustomerFields = ({ form, updateField, setForm, t, section, shopify }) => {
  const showPersonal = section !== 'address';
  const showAddress = section !== 'personal';
  const [addrSuggestions, setAddrSuggestions] = useState([]);
  const [addrLoading, setAddrLoading] = useState(false);
  const [addrFocused, setAddrFocused] = useState(false);
  const debounceRef = useRef(null);

  const onAddressChange = (v) => {
    updateField('address1', v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!v || v.length < 3) {
      setAddrSuggestions([]);
      return;
    }
    setAddrLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await publicApi.get(`${API}/public/address-search?q=${encodeURIComponent(v)}&limit=5&country=${encodeURIComponent(form.country || 'FR')}`);
        setAddrSuggestions(r.data.results || []);
      } catch {
        setAddrSuggestions([]);
      } finally {
        setAddrLoading(false);
      }
    }, 250);
  };

  const pickSuggestion = (s) => {
    setForm((p) => ({
      ...p,
      address1: s.address1 || p.address1,
      city: s.city || p.city,
      postal_code: s.postal_code || p.postal_code,
      country: s.country || p.country,
    }));
    setAddrSuggestions([]);
    setAddrFocused(false);
  };

  return (
    <div className="space-y-4">
      {showPersonal && (<>
      <div>
        <Label htmlFor="email" className="text-xs text-zinc-600">{t.email}</Label>
        <Input id="email" type="email" name="email" autoComplete="email" data-testid={CHECKOUT.emailInput}
          value={form.email} onChange={(e) => updateField('email', e.target.value)}
          className="mt-1 h-12 rounded-md text-base" placeholder={t.email_ph} required />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="fn" className="text-xs text-zinc-600">{t.first_name}</Label>
          <Input id="fn" name="given-name" autoComplete="given-name" data-testid={CHECKOUT.firstNameInput}
            value={form.first_name} onChange={(e) => updateField('first_name', e.target.value)}
            className="mt-1 h-12 rounded-md text-base" required />
        </div>
        <div>
          <Label htmlFor="ln" className="text-xs text-zinc-600">{t.last_name}</Label>
          <Input id="ln" name="family-name" autoComplete="family-name" data-testid={CHECKOUT.lastNameInput}
            value={form.last_name} onChange={(e) => updateField('last_name', e.target.value)}
            className="mt-1 h-12 rounded-md text-base" required />
        </div>
      </div>
      </>)}
      {showAddress && (<>
      <div className="relative">
        <Label htmlFor="addr" className="text-xs text-zinc-600">{t.address}</Label>
        {shopify && <Search className="absolute left-3 top-[34px] h-4 w-4 text-zinc-400 pointer-events-none z-10" />}
        <Input
          id="addr" name="address-line1" autoComplete="street-address" data-testid={CHECKOUT.addressInput}
          value={form.address1}
          onChange={(e) => onAddressChange(e.target.value)}
          onFocus={() => setAddrFocused(true)}
          onBlur={() => setTimeout(() => setAddrFocused(false), 200)}
          className={`mt-1 h-12 rounded-md text-base ${shopify ? 'pl-9' : ''}`}
          placeholder={shopify ? 'Rechercher une adresse…' : t.address_ph}
          required
        />
        {addrFocused && (addrLoading || addrSuggestions.length > 0) && (
          <div
            data-testid="checkout-address-suggestions"
            className="absolute z-30 mt-1 w-full bg-white border border-zinc-200 rounded-md shadow-lg max-h-64 overflow-auto"
          >
            {addrLoading && (
              <div className="px-3 py-2 text-xs text-zinc-400 flex items-center gap-2">
                <Loader2 className="h-3 w-3 animate-spin" /> {t.searching}
              </div>
            )}
            {addrSuggestions.map((s, i) => (
              <button
                key={`${s.label}-${i}`}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pickSuggestion(s)}
                data-testid={`checkout-address-option-${i}`}
                className="w-full text-left px-3 py-2 hover:bg-zinc-50 border-b border-zinc-100 last:border-b-0"
              >
                <div className="text-sm font-medium">{s.address1}</div>
                <div className="text-xs text-zinc-500">{s.postal_code} {s.city} · {s.context}</div>
              </button>
            ))}
            {!addrLoading && addrSuggestions.length === 0 && form.address1.length >= 3 && (
              <div className="px-3 py-2 text-xs text-zinc-400">{t.no_result}</div>
            )}
          </div>
        )}
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2">
          <Label htmlFor="city" className="text-xs text-zinc-600">{t.city}</Label>
          <Input id="city" name="address-level2" autoComplete="address-level2" data-testid={CHECKOUT.cityInput}
            value={form.city} onChange={(e) => updateField('city', e.target.value)}
            className="mt-1 h-12 rounded-md text-base" required />
        </div>
        <div>
          <Label htmlFor="zip" className="text-xs text-zinc-600">{t.postal_code}</Label>
          <Input id="zip" name="postal-code" autoComplete="postal-code" data-testid={CHECKOUT.zipInput}
            value={form.postal_code} onChange={(e) => updateField('postal_code', e.target.value)}
            className="mt-1 h-12 rounded-md text-base" required />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="country" className="text-xs text-zinc-600">{t.country}</Label>
          <select
            id="country"
            name="country"
            autoComplete="country"
            data-testid={CHECKOUT.countryInput}
            value={form.country || 'FR'}
            onChange={(e) => updateField('country', e.target.value)}
            required
            className="mt-1 h-12 w-full rounded-md border border-zinc-200 bg-white px-3 text-base focus:outline-none focus:ring-2 focus:ring-zinc-900/10"
          >
            {COUNTRIES.map((c) => (
              <option key={c.code} value={c.code}>{c.name}</option>
            ))}
          </select>
        </div>
        <div>
          <Label htmlFor="phone" className="text-xs text-zinc-600">{t.phone}</Label>
          <Input id="phone" name="tel" type="tel" autoComplete="tel" data-testid={CHECKOUT.phoneInput}
            value={form.phone} onChange={(e) => updateField('phone', e.target.value)}
            className="mt-1 h-12 rounded-md text-base" />
        </div>
      </div>
      </>)}
    </div>
  );
};

const ShippingPicker = ({ options, value, onChange, accentColor, t }) => {
  if (!options || options.length === 0) {
    return (
      <div data-testid="checkout-shipping-empty" className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">
        {t.no_shipping}
      </div>
    );
  }
  return (
    <div className="space-y-2.5" data-testid="checkout-shipping-picker">
      {options.map((o) => {
        const selected = value === o.id;
        const imgUrl = resolveImageUrl(o.image_url);
        return (
          <label
            key={o.id}
            data-testid={`checkout-shipping-option-${o.id}`}
            data-selected={selected}
            className={`flex gap-3 items-center p-3 border rounded-md cursor-pointer transition
              ${selected ? 'border-zinc-950 bg-zinc-50 ring-2' : 'border-zinc-200 hover:border-zinc-300 bg-white'}`}
            style={selected ? { borderColor: accentColor || '#0A0A0A', '--tw-ring-color': accentColor || '#0A0A0A' } : {}}
          >
            <input
              type="radio"
              name="shipping_option"
              checked={selected}
              onChange={() => onChange(o.id)}
              className="sr-only"
              data-testid={`checkout-shipping-radio-${o.id}`}
            />
            <span
              className={`w-5 h-5 shrink-0 rounded-full border-2 flex items-center justify-center transition
                ${selected ? '' : 'border-zinc-300'}`}
              style={selected ? { borderColor: accentColor || '#0A0A0A' } : {}}
            >
              {selected && (
                <span
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: accentColor || '#0A0A0A' }}
                />
              )}
            </span>
            {imgUrl && (
              <img src={imgUrl} alt="" className="w-12 h-12 object-contain rounded border border-zinc-100 shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-zinc-900">{o.name}</div>
              {o.description && <div className="text-xs text-zinc-500 mt-0.5">{o.description}</div>}
            </div>
            <div className="font-mono text-sm font-semibold shrink-0">
              {o.price === 0 ? t.free : money(o.price)}
            </div>
          </label>
        );
      })}
    </div>
  );
};

const PayForm = ({ total, amountCents, items, form, hasRequiredCustomerInfo, source, shippingOptionId, shippingChosen, accentColor, storeId, storeSlug, t, payLabel, lumen, shopify, brand }) => {
  const stripe = useStripe();
  const elements = useElements();
  const nav = useNavigate();
  const [processing, setProcessing] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const [ready, setReady] = useState(false);
  const [elemComplete, setElemComplete] = useState(false);  // card fields fully filled?
  // Remembers the PaymentIntent already created for this cart/amount so a retry
  // (after a decline) reuses it instead of creating a brand-new "Incomplet" PI.
  const piRef = useRef(null);

  // Keep the deferred Element's amount in sync with the live cart total
  // (changes when the customer picks/swaps a shipping option). We never update
  // mid-payment: doing so can corrupt the Elements store and throw
  // "Could not retrieve elements store" during submit()/confirmPayment().
  useEffect(() => {
    if (elements && amountCents >= 50 && !processing) {
      elements.update({ amount: amountCents });
    }
  }, [elements, amountCents, processing]);

  const pay = async (e) => {
    e.preventDefault();
    if (processing) return;                 // guard against double-tap / double submit
    if (!stripe || !elements || !ready) {   // never confirm before the element is mounted
      toast.error(t.loading_payment);
      return;
    }
    if (!shippingChosen) {
      setErrMsg(t.err_choose_shipping);
      toast.error(t.toast_choose_shipping);
      return;
    }
    if (!hasRequiredCustomerInfo) {
      setErrMsg(t.err_complete_info);
      toast.error(t.toast_incomplete_info);
      return;
    }
    if (!elemComplete) {   // never create a PI before the card is actually entered
      setErrMsg(t.err_check_payment);
      toast.error(t.err_check_payment);
      return;
    }
    setProcessing(true);
    setErrMsg('');
    trackEvent('payment_attempted', { storeId, slug: storeSlug, amount: total, source });
    try {
      // 1. Validate the Payment Element (deferred flow requires submit() first).
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setErrMsg(submitError.message || t.err_check_payment);
        setProcessing(false);
        return;
      }
      // 2. Reuse the existing PaymentIntent when the customer retries with the
      //    same amount — this avoids spawning a new "Incomplet" PI on every
      //    click. A new PI is created only when none exists yet or the amount
      //    changed (e.g. the customer switched shipping option).
      let clientSecret;
      let piId;
      if (piRef.current && piRef.current.amountCents === amountCents) {
        clientSecret = piRef.current.clientSecret;
        piId = piRef.current.piId;
      } else {
        const { data } = await publicApi.post(`${API}/public/checkout/create-payment-intent`, {
          items,
          customer: {
            email: form.email,
            first_name: form.first_name,
            last_name: form.last_name,
            address1: form.address1,
            city: form.city,
            postal_code: form.postal_code,
            country: (form.country || 'FR').toUpperCase(),
            phone: form.phone || '',
          },
          shipping_option_id: shippingOptionId,
          source,
          attribution: getAttribution(),
          promo_code: getActivePromo(),
        });
        clientSecret = data.client_secret;
        piId = data.payment_intent_id;
        piRef.current = { clientSecret, piId, amountCents };
        trackEvent('checkout_initialized', { storeId, slug: storeSlug, paymentIntentId: piId, amount: data.amount, source });
      }
      // 3. Confirm the payment. The PaymentElement is created with
      //    billingDetails:'never', so Stripe REQUIRES us to pass the full billing
      //    details here (name, email, phone, address incl. postal_code) — omitting
      //    postal_code/phone makes confirmPayment hard-fail, and Klarna/iDEAL need
      //    the complete address. We use the customer's delivery address as billing.
      const billing_details = {
        name: `${(form.first_name || '').trim()} ${(form.last_name || '').trim()}`.trim() || '',
        email: form.email || '',
        phone: form.phone || '',
        address: {
          line1: form.address1 || '',
          city: form.city || '',
          state: form.state || '',
          postal_code: form.postal_code || '',
          country: (form.country || 'FR').toUpperCase(),
        },
      };
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: `${window.location.origin}/checkout/success?pi=${piId}`,
          payment_method_data: { billing_details },
        },
        redirect: 'if_required',
      });
      if (error) {
        trackEvent('payment_failed', {
          storeId, slug: storeSlug, paymentIntentId: piId, amount: total, source,
          failureCode: error.code || error.decline_code || 'unknown',
          failureMessage: (error.message || '').slice(0, 240),
        });
        setErrMsg(error.message || t.err_payment);
        toast.error(error.message || t.err_payment);
        setProcessing(false);
        return;
      }
      // Redirect-based methods (iDEAL, Bancontact…) can resolve here in
      // 'requires_action' WITHOUT auto-redirecting. We must push the customer to
      // their bank to finish authorization — otherwise the intent sits in
      // requires_action and Stripe expires it (~the "Expiré" iDEAL payments).
      if (paymentIntent && paymentIntent.status === 'requires_action') {
        const { error: naError } = await stripe.handleNextAction({ clientSecret });
        if (naError) {
          trackEvent('payment_failed', {
            storeId, slug: storeSlug, paymentIntentId: piId, amount: total, source,
            failureCode: naError.code || 'requires_action_failed',
            failureMessage: (naError.message || '').slice(0, 240),
          });
          setErrMsg(naError.message || t.err_payment);
          toast.error(naError.message || t.err_payment);
          setProcessing(false);
          return;
        }
        // For redirect methods handleNextAction navigates away (this line is not
        // reached). If it resolved inline, fall through to the status page which
        // polls the final outcome.
      }
      trackEvent('payment_succeeded', {
        storeId, slug: storeSlug,
        paymentIntentId: (paymentIntent && paymentIntent.id) || piId,
        amount: total, source,
      });
      nav(`/checkout/success?pi=${(paymentIntent && paymentIntent.id) || piId}`);
    } catch (e) {
      const msg = extractErrorMessage(e, t.err_generic);
      trackEvent('payment_failed', {
        storeId, slug: storeSlug, amount: total, source,
        failureCode: 'network_or_create_error', failureMessage: msg,
      });
      setErrMsg(msg);
      toast.error(msg);
      setProcessing(false);
    }
  };

  const disabled = !stripe || processing || !shippingChosen || !ready || !elemComplete;
  const payBg = shippingChosen ? (accentColor || ((lumen || shopify) ? '#111111' : '#0A0A0A')) : '#A1A1AA';
  const btnLabel = processing ? (
    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t.processing}</>
  ) : shippingChosen ? (
    brand ? <>{payLabel || t.pay} {money(total)}</> : (lumen || shopify)
      ? <>{payLabel || t.pay} • {money(total)} <Lock className="h-[18px] w-[18px] ml-2" /></>
      : <><Lock className="h-4 w-4 mr-2" /> {payLabel || t.pay} {money(total)}</>
  ) : (
    <>{t.choose_shipping_btn}</>
  );

  // We collect the billing address in our own form and pass it to Stripe at
  // confirm time (for AVS / Radar), so the PaymentElement must NOT collect it
  // again — otherwise Stripe rejects the duplicated billing_details.
  const peFields = { fields: { billingDetails: 'never' } };
  const peOptions = (lumen || shopify)
    ? { layout: { type: 'accordion', defaultCollapsed: false, radios: 'if_multiple', spacedAccordionItems: true }, wallets: { link: 'never', applePay: 'never', googlePay: 'never' }, ...peFields }
    : { layout: 'tabs', wallets: { link: 'never', applePay: 'never', googlePay: 'never' }, ...peFields };

  const btnRadius = shopify ? 'rounded-lg' : lumen ? 'rounded-xl' : 'rounded-md';

  if (brand) {
    return (
      <form onSubmit={pay} className="space-y-5">
        <PaymentElement onReady={() => setReady(true)} onChange={(ev) => setElemComplete(!!ev.complete)} options={peOptions} />
        {errMsg && <div data-testid={CHECKOUT.errorMessage} className="text-xs text-red-700 bg-red-50 border border-red-200 p-3 rounded-md">{errMsg}</div>}
        <Button type="submit" disabled={disabled} data-testid="checkout-pay-button" style={{ backgroundColor: payBg }}
          className="flex w-full hover:opacity-90 text-white transition active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed items-center justify-center rounded-xl h-14 text-[18px] font-semibold">
          {btnLabel}
        </Button>
        {ready && shippingChosen && !elemComplete && <p className="text-[11px] text-zinc-400 text-center -mt-2" data-testid="checkout-card-hint">{t.card_gate_hint}</p>}
      </form>
    );
  }

  return (
    <form onSubmit={pay} className="space-y-5">
      <PaymentElement onReady={() => setReady(true)} onChange={(ev) => setElemComplete(!!ev.complete)} options={peOptions} />
      {errMsg && (
        <div data-testid={CHECKOUT.errorMessage} className="text-xs text-red-700 bg-red-50 border border-red-200 p-3 rounded-md">
          {errMsg}
        </div>
      )}
      {/* Desktop inline pay button */}
      <Button
        type="submit"
        disabled={disabled}
        data-testid="checkout-pay-button"
        style={{ backgroundColor: payBg }}
        className={`hidden lg:flex w-full hover:opacity-90 text-white transition disabled:opacity-60 disabled:cursor-not-allowed items-center justify-center ${(lumen || shopify) ? `${btnRadius} h-14 text-[17px] font-semibold` : 'rounded-md h-12 text-base font-semibold'}`}
      >
        {btnLabel}
      </Button>
      {ready && shippingChosen && !elemComplete && (
        <p className="hidden lg:block text-[11px] text-zinc-400 text-center -mt-2" data-testid="checkout-card-hint">
          {t.card_gate_hint}
        </p>
      )}
      {!shopify && (
        <p className="hidden lg:block text-xs text-zinc-500 text-center">
          {t.terms}
        </p>
      )}
      <p className="lg:hidden text-xs text-zinc-500 text-center flex items-center justify-center gap-1.5">
        <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" /> {ready && shippingChosen && !elemComplete ? t.card_gate_hint : t.secure_pay}
      </p>

      {/* Mobile sticky bottom pay bar (Shopify-style) */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-t border-zinc-200 px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] shadow-[0_-8px_30px_rgba(0,0,0,0.06)]">
        <Button
          type="submit"
          disabled={disabled}
          data-testid="mobile-sticky-pay-button"
          style={{ backgroundColor: payBg }}
          className={`w-full hover:opacity-90 text-white transition active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center h-14 text-[18px] font-semibold ${lumen ? 'rounded-xl' : 'rounded-md'}`}
        >
          {btnLabel}
        </Button>
      </div>
    </form>
  );
};


// Hosted redirect modes — "invoice" (facture Stripe) or "link" (Checkout
// Session unique). The backend builds the hosted object and we redirect the
// buyer to Stripe's payment page (global no-referrer meta applies). Order
// recording happens server-side (webhook + scheduler + success-page poll).
const StripeInvoicePay = ({ mode = 'invoice', total, items, form, hasRequiredCustomerInfo, source, shippingOptionId, shippingChosen, accentColor, storeId, storeSlug, t, payLabel, lumen, brand }) => {
  const [processing, setProcessing] = useState(false);
  const [errMsg, setErrMsg] = useState('');

  const pay = async (e) => {
    e.preventDefault();
    if (processing) return;
    if (!shippingChosen) {
      setErrMsg(t.err_choose_shipping);
      toast.error(t.toast_choose_shipping);
      return;
    }
    if (!hasRequiredCustomerInfo) {
      setErrMsg(t.err_complete_info);
      toast.error(t.toast_incomplete_info);
      return;
    }
    setProcessing(true);
    setErrMsg('');
    trackEvent('payment_attempted', { storeId, slug: storeSlug, amount: total, source });
    try {
      const endpoint = mode === 'link' ? 'create-session' : 'create-invoice';
      const { data } = await publicApi.post(`${API}/public/checkout/${endpoint}`, {
        items,
        customer: {
          email: form.email,
          first_name: form.first_name,
          last_name: form.last_name,
          address1: form.address1,
          city: form.city,
          postal_code: form.postal_code,
          country: (form.country || 'FR').toUpperCase(),
          phone: form.phone || '',
        },
        shipping_option_id: shippingOptionId,
        source,
        attribution: getAttribution(),
        promo_code: getActivePromo(),
      });
      const payUrl = mode === 'link' ? data.session_url : data.invoice_url;
      trackEvent('checkout_initialized', { storeId, slug: storeSlug, paymentIntentId: data.invoice_id || data.session_id, amount: data.amount, source });
      if (!payUrl) throw new Error(t.err_payment);
      window.location.href = payUrl;
    } catch (err) {
      const msg = extractErrorMessage(err, t.err_generic);
      trackEvent('payment_failed', {
        storeId, slug: storeSlug, amount: total, source,
        failureCode: 'invoice_create_error', failureMessage: msg,
      });
      setErrMsg(msg);
      toast.error(msg);
      setProcessing(false);
    }
  };

  const payBg = shippingChosen ? (accentColor || (lumen ? '#111111' : '#0A0A0A')) : '#A1A1AA';
  const btnLabel = processing ? (
    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t.processing}</>
  ) : shippingChosen ? (
    lumen
      ? <>{payLabel || t.pay} • {money(total)} <Lock className="h-[18px] w-[18px] ml-2" /></>
      : <><Lock className="h-4 w-4 mr-2" /> {payLabel || t.pay} {money(total)}</>
  ) : (
    <>{t.choose_shipping_btn}</>
  );
  const disabled = processing || !shippingChosen;

  if (brand) {
    return (
      <form onSubmit={pay} className="space-y-5">
        <div className="flex items-center gap-3 border p-4 rounded-xl border-zinc-200 bg-zinc-50" data-testid="checkout-invoice-info">
          <ShieldCheck className="h-5 w-5 text-emerald-600 shrink-0" />
          <div className="text-xs text-zinc-600">{t.secure_pay} — <span className="font-semibold text-zinc-900">Stripe</span></div>
        </div>
        {errMsg && <div data-testid={CHECKOUT.errorMessage} className="text-xs text-red-700 bg-red-50 border border-red-200 p-3 rounded-md">{errMsg}</div>}
        <Button type="submit" disabled={disabled} data-testid="checkout-invoice-pay-button" style={{ backgroundColor: payBg }}
          className="flex w-full hover:opacity-90 text-white transition active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed items-center justify-center rounded-xl h-14 text-[18px] font-semibold">
          {shippingChosen && !processing ? <>{payLabel || t.pay} {money(total)}</> : btnLabel}
        </Button>
      </form>
    );
  }

  return (
    <form onSubmit={pay} className="space-y-5">
      <div className={`flex items-center gap-3 border p-4 ${lumen ? 'rounded-xl' : 'rounded-md'} border-zinc-200 bg-zinc-50`} data-testid="checkout-invoice-info">
        <ShieldCheck className="h-5 w-5 text-emerald-600 shrink-0" />
        <div className="text-xs text-zinc-600">{t.secure_pay} — <span className="font-semibold text-zinc-900">Stripe</span></div>
      </div>
      {errMsg && (
        <div data-testid={CHECKOUT.errorMessage} className="text-xs text-red-700 bg-red-50 border border-red-200 p-3 rounded-md">
          {errMsg}
        </div>
      )}
      <Button
        type="submit"
        disabled={disabled}
        data-testid="checkout-invoice-pay-button"
        style={{ backgroundColor: payBg }}
        className={`hidden lg:flex w-full hover:opacity-90 text-white transition disabled:opacity-60 disabled:cursor-not-allowed items-center justify-center ${lumen ? 'rounded-xl h-14 text-[18px] font-semibold' : 'rounded-md h-12 text-base font-semibold'}`}
      >
        {btnLabel}
      </Button>
      <p className="hidden lg:block text-xs text-zinc-500 text-center">
        {t.terms}
      </p>
      <p className="lg:hidden text-xs text-zinc-500 text-center flex items-center justify-center gap-1.5">
        <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" /> {t.secure_pay}
      </p>

      {/* Mobile sticky bottom pay bar (Shopify-style) */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-t border-zinc-200 px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] shadow-[0_-8px_30px_rgba(0,0,0,0.06)]">
        <Button
          type="submit"
          disabled={disabled}
          data-testid="mobile-sticky-pay-button"
          style={{ backgroundColor: payBg }}
          className={`w-full hover:opacity-90 text-white transition active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center h-14 text-[18px] font-semibold ${lumen ? 'rounded-xl' : 'rounded-md'}`}
        >
          {btnLabel}
        </Button>
      </div>
    </form>
  );
};


// Express checkout (Apple Pay / Google Pay) at the top of the form. Mounted in
// its own <Elements> so elements.submit() never validates the card element.
// Renders nothing when no wallet is available on the device.
const ExpressCheckout = ({
  amountCents, total, items, source, selectedShipping, shippingOptionId,
  accentColor, storeId, storeSlug, t, hideDivider, onAvailable,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const nav = useNavigate();
  const [available, setAvailable] = useState(false);
  // When no wallet is available (e.g. in-app browsers like Instagram/TikTok, or
  // desktop without Apple/Google Pay), fully UNMOUNT the Express Checkout element
  // instead of just hiding it. Keeping it mounted holds heavy wallet iframes/SDKs
  // in memory — on iOS/WebKit this contributes to the tab crashing & reloading.
  const [dead, setDead] = useState(false);

  useEffect(() => {
    if (elements && amountCents >= 50) elements.update({ amount: amountCents });
  }, [elements, amountCents]);

  const shippingRates = [{
    id: selectedShipping?.id || 'default',
    displayName: selectedShipping?.name || t.shipping,
    amount: Math.max(0, Math.round((selectedShipping?.price || 0) * 100)),
  }];

  const onClick = ({ resolve }) => {
    // NOTE: lineItems = total MINUS shipping, because Stripe adds the selected
    // shippingRate back on top. lineItems + shippingRate must equal `total`.
    // Do not "simplify" by removing the shipping subtraction — it would double it.
    resolve({
      emailRequired: true,
      phoneNumberRequired: true,
      shippingAddressRequired: true,
      shippingRates,
      lineItems: [{ name: t.summary, amount: Math.max(50, Math.round((total - (selectedShipping?.price || 0)) * 100)) }],
    });
  };

  const onConfirm = async (event) => {
    if (!stripe || !elements) return;
    try {
      const { error: submitError } = await elements.submit();
      if (submitError) { toast.error(submitError.message || t.err_payment); return; }

      const sa = event.shippingAddress || {};
      const addr = sa.address || {};
      const bd = event.billingDetails || {};
      const fullName = sa.name || bd.name || '';
      const parts = fullName.trim().split(/\s+/);
      const customer = {
        email: bd.email || '',
        first_name: parts[0] || 'Client',
        last_name: parts.slice(1).join(' ') || '-',
        address1: addr.line1 || '',
        city: addr.city || '',
        postal_code: addr.postal_code || '',
        country: (addr.country || 'FR').toUpperCase(),
        phone: bd.phone || '',
      };
      trackEvent('payment_attempted', { storeId, slug: storeSlug, amount: total, source });
      const { data } = await publicApi.post(`${API}/public/checkout/create-payment-intent`, {
        items, customer, shipping_option_id: shippingOptionId, source, attribution: getAttribution(), promo_code: getActivePromo(),
      });
      const piId = data.payment_intent_id;
      trackEvent('checkout_initialized', { storeId, slug: storeSlug, paymentIntentId: piId, amount: data.amount, source });
      const billing_details = {
        name: fullName.trim() || '',
        email: bd.email || '',
        phone: bd.phone || '',
        address: {
          line1: addr.line1 || '',
          city: addr.city || '',
          state: addr.state || '',
          postal_code: addr.postal_code || '',
          country: (addr.country || 'FR').toUpperCase(),
        },
      };
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        clientSecret: data.client_secret,
        confirmParams: {
          return_url: `${window.location.origin}/checkout/success?pi=${piId}`,
          payment_method_data: { billing_details },
        },
        redirect: 'if_required',
      });
      if (error) {
        trackEvent('payment_failed', {
          storeId, slug: storeSlug, paymentIntentId: piId, amount: total, source,
          failureCode: error.code || 'unknown', failureMessage: (error.message || '').slice(0, 240),
        });
        toast.error(error.message || t.err_payment);
        return;
      }
      if (paymentIntent && paymentIntent.status === 'requires_action') {
        const { error: naError } = await stripe.handleNextAction({ clientSecret: data.client_secret });
        if (naError) {
          toast.error(naError.message || t.err_payment);
          return;
        }
      }
      trackEvent('payment_succeeded', {
        storeId, slug: storeSlug, paymentIntentId: (paymentIntent && paymentIntent.id) || piId, amount: total, source,
      });
      nav(`/checkout/success?pi=${(paymentIntent && paymentIntent.id) || piId}`);
    } catch (e) {
      toast.error(extractErrorMessage(e, t.err_payment));
    }
  };

  if (dead) return null;
  return (
    <div className={available ? 'mb-6' : 'hidden'} data-testid="express-checkout-zone">
      <ExpressCheckoutElement
        options={{
          buttonHeight: 50,
          // Keep only the lightweight native wallets (Apple Pay on iOS / Google
          // Pay on Android). Amazon Pay & PayPal load heavy third-party SDKs and
          // extra iframes that blow the iOS/WebKit memory budget on the checkout,
          // causing the page to crash & reload in a loop. Link is also off.
          paymentMethods: {
            applePay: 'auto',
            googlePay: 'auto',
            amazonPay: 'never',
            paypal: 'never',
            link: 'never',
          },
        }}
        onReady={(e) => {
          const has = !!(e && e.availablePaymentMethods);
          setAvailable(has);
          if (onAvailable) onAvailable(has);
          if (!has) setDead(true); // free wallet iframes when nothing is offered
        }}
        onClick={onClick}
        onConfirm={onConfirm}
        onShippingAddressChange={({ resolve }) => resolve()}
        onShippingRateChange={({ resolve }) => resolve()}
      />
      {available && !hideDivider && (
        <div
          data-testid="express-checkout-divider"
          className="flex items-center gap-4 text-[10px] text-zinc-400 uppercase tracking-widest my-5 before:flex-1 before:border-t before:border-zinc-200 after:flex-1 after:border-t after:border-zinc-200"
        >
          {t.or_pay_card}
        </div>
      )}
    </div>
  );
};

// Mobile collapsible order summary (Shopify-style). Total always visible; tap
// to expand and reveal line items + breakdown. Hidden on desktop (lg+).
const MobileSummaryBar = ({
  open, onToggle, cartLines, lineDiscounts, subtotal, bogoDiscount,
  summaryOfferLabel, selectedShipping, total, accentColor, t, promoProps, promoDiscount,
}) => {
  const lineGross = (line) => line.product.price * line.quantity;
  return (
    <div className="lg:hidden bg-zinc-50 border-b border-zinc-200 sticky top-0 z-40">
      <button
        type="button"
        onClick={onToggle}
        data-testid="mobile-summary-toggle"
        className="w-full px-4 py-3 flex items-center justify-between text-sm transition-colors hover:bg-zinc-100"
      >
        <span className="flex items-center gap-2 font-medium text-zinc-700">
          <ShoppingBag className="h-4 w-4" style={{ color: accentColor || '#0A0A0A' }} />
          {open ? t.summary : `${t.summary} · ${cartLines.reduce((s, l) => s + l.quantity, 0)}`}
          <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
        </span>
        <span className="text-lg font-bold font-mono text-zinc-900">{money(total)}</span>
      </button>
      {promoProps && <div className="px-4 pb-3"><PromoCodeField {...promoProps} accent={accentColor} tidSuffix="-mobile" /></div>}
      {open && (
        <div className="px-4 pb-4 space-y-3 animate-in fade-in slide-in-from-top-2 duration-200" data-testid="mobile-summary-details">
          {cartLines.map((line, idx) => {
            const displayImage = line.variant?.image || line.product.image_url;
            const lineDisc = lineDiscounts[idx] || 0;
            const lineNet = lineGross(line) - lineDisc;
            return (
              <div key={`${line.product.id}-${idx}`} className="flex gap-3 items-center">
                {displayImage ? (
                  <img src={displayImage} alt="" className="w-12 h-12 object-cover rounded-md border border-zinc-200" />
                ) : <div className="w-12 h-12 bg-zinc-100 rounded-md" />}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{line.product.name}</div>
                  <div className="text-xs text-zinc-500">{t.qty} {line.quantity}{line.variant?.title ? ` · ${line.variant.title}` : ''}</div>
                </div>
                <div className="text-sm font-mono shrink-0">
                  {lineDisc > 0 ? (
                    <span className="text-emerald-700">{money(lineNet)}</span>
                  ) : money(lineGross(line))}
                </div>
              </div>
            );
          })}
          <div className="border-t border-zinc-200 pt-3 space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-zinc-600">{t.subtotal}</span><span className="font-mono">{money(subtotal)}</span></div>
            {bogoDiscount > 0 && (
              <div className="flex justify-between text-emerald-700"><span>{summaryOfferLabel}</span><span className="font-mono">−{money(bogoDiscount)}</span></div>
            )}
            {promoProps && <PromoSummaryRow promo={promoProps.promo} discount={promoDiscount} lang={promoProps.lang} money={money} />}
            <div className="flex justify-between"><span className="text-zinc-600">{selectedShipping?.name || t.shipping}</span><span className="font-mono">{selectedShipping ? money(selectedShipping.price) : t.to_choose}</span></div>
          </div>
        </div>
      )}
    </div>
  );
};


// ─────────────────────────────────────────────────────────────────────────────
// Trustpilot reassurance block — shared across all templates (classic/lumen/
// shopify). Rendered only when the store enabled it in the admin.
// ─────────────────────────────────────────────────────────────────────────────
const TrustpilotBlock = ({ ui, className = '', testId = 'trustpilot-block' }) => {
  if (!ui?.trustpilot_enabled) return null;
  return (
    <div className={`flex flex-col items-center justify-center gap-1.5 p-5 bg-[#FDFBF9] border border-zinc-200 rounded-xl ${className}`} data-testid={testId}>
      <span className="text-sm font-bold">{ui.trustpilot_label || 'Excellent'}</span>
      <div className="flex gap-0.5">
        {[0, 1, 2, 3, 4].map((s) => (
          <span key={s} className="w-6 h-6 bg-[#00B67A] flex items-center justify-center rounded-sm"><Star className="w-4 h-4 text-white fill-white" /></span>
        ))}
      </div>
      <span className="text-xs text-zinc-500">{ui.trustpilot_rating || '4.8'} / 5{ui.trustpilot_reviews ? ` · ${ui.trustpilot_reviews}` : ''}</span>
      <span className="inline-flex items-center gap-1 text-sm font-semibold text-zinc-800 mt-1"><Star className="w-4 h-4 text-[#00B67A] fill-[#00B67A]" /> Trustpilot</span>
    </div>
  );
};


// ─────────────────────────────────────────────────────────────────────────────
// LUMEN — alternative checkout template (selectable per store). Reuses the same
// functional sub-components (ExpressCheckout / CustomerFields / ShippingPicker /
// PayForm / VivaPayForm) so the payment logic is identical; only the layout,
// header, step indicator, trust blocks, Trustpilot and footer differ.
// ─────────────────────────────────────────────────────────────────────────────
const LumenCheckout = ({ vm }) => {
  const {
    branding, t, pixels, cartLines, lineDiscounts, subtotal, bogoDiscount,
    summaryOfferLabel, selectedShipping, total, offerTag, skipped, isCartMode,
    quantity, setQuantity, psp, vivaCfg, payzenCfg, platform, stripeInstance, elementsOptions,
    form, updateField, setForm, hasRequiredCustomerInfo, shippingOptions,
    selectedShippingId, setSelectedShippingId, source, summaryOpen, setSummaryOpen,
    stripeItems, isPreview, geoLang, paymentMode, currency, promo, promoDiscount, promoProps,
  } = vm;

  const lang = resolveLang(branding.language, geoLang);
  const lt = getLumenT(lang);
  const ui = branding.checkout_ui || {};
  const accent = branding.accent_color || '#111111';
  const lineGross = (line) => line.product.price * line.quantity;

  // Lumen palette (spec): bg #FFFFFF · card #F8F8F8 · border #E5E7EB ·
  // title #111111 · text #4B5563 · button #111111/#000 · green #16A34A.
  const C = { card: '#F8F8F8', border: '#E5E7EB', title: '#111111', text: '#4B5563', green: '#16A34A' };

  // Per-language merchant overrides: use the override for the resolved checkout
  // language when present, otherwise fall back to the built-in translated copy.
  // This lets merchants customise EACH language while everything still follows
  // the selected language (no EN/FR mix).
  const loc = (ui.i18n && ui.i18n[lang]) || {};
  const helpText = loc.help_text || lt.help;
  const guaranteeText = loc.guarantee_text || lt.guarantee;
  const payLabel = loc.pay_button_text || lt.complete_order;
  const showPromo = ui.show_promo_bar !== false;
  const promoItems = (loc.promo_bar_items && loc.promo_bar_items.length)
    ? loc.promo_bar_items
    : [t.trust_secure, t.trust_fast_ship, t.trust_money_back];
  const trustItems = (loc.trust_items && loc.trust_items.length) ? loc.trust_items : lt.trust;
  const footerItems = (loc.footer_items && loc.footer_items.length) ? loc.footer_items : lt.footer;
  const showSteps = ui.show_steps !== false;
  const showExpress = ui.show_express !== false;
  const tpEnabled = !!ui.trustpilot_enabled;

  const steps = [lt.step_data, lt.step_payment, lt.step_confirm];
  const cardCls = 'rounded-2xl p-4 sm:p-5';
  const cardStyle = { backgroundColor: '#FFFFFF', border: `1px solid ${C.border}` };
  const SectionHeader = ({ icon: Icon, n, title }) => (
    <h2 className="text-[16px] font-semibold flex items-center gap-2.5 mb-4" style={{ color: C.title }}>
      <span className="w-8 h-8 rounded-lg bg-white flex items-center justify-center shrink-0" style={{ border: `1px solid ${C.border}` }}>
        <Icon className="h-[18px] w-[18px]" style={{ color: C.title }} />
      </span>
      <span><span style={{ color: C.text }}>{n}.</span> {title}</span>
    </h2>
  );

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#FFFFFF', color: C.text, fontFamily: 'Inter, system-ui, sans-serif' }} data-testid="lumen-checkout-form">
      {pixels.store_id && !isPreview && (
        <PixelTracker storeId={pixels.store_id} slug={pixels.store_slug} event="page_view_checkout" metaPixelId={pixels.meta_pixel_id} tiktokPixelId={pixels.tiktok_pixel_id} googleTagId={pixels.google_tag_id} googleLabel={pixels.google_conversion_label} />
      )}

      {/* Header */}
      <header className="sticky top-0 z-50 h-16 w-full flex items-center justify-between gap-2 px-6 md:px-8 bg-white/90 backdrop-blur-md" style={{ borderBottom: `1px solid ${C.border}` }}>
        <div className="flex items-center gap-2 shrink-0" data-testid="lumen-store-brand">
          {branding.logo_url ? (
            <img src={resolveImageUrl(branding.logo_url)} alt={branding.display_name} className="h-8 max-w-[140px] object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          ) : (
            <span className="font-display font-extrabold tracking-tight text-2xl uppercase truncate" style={{ color: C.title }}>{branding.display_name || t.store_fallback}</span>
          )}
        </div>
        <div className="flex items-center gap-1.5 text-[13px] sm:text-sm font-medium min-w-0" style={{ color: C.text }}>
          <Lock className="h-4 w-4 shrink-0" /> <span className="truncate">{lt.secure_order}</span>
        </div>
        {branding.support_email ? (
          <a href={`mailto:${branding.support_email}`} title={helpText} aria-label={helpText} data-testid="lumen-help-link"
            className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center hover:bg-zinc-50 transition" style={{ border: `1px solid ${C.border}`, color: C.text }}>
            <HelpCircle className="h-5 w-5" />
          </a>
        ) : (
          <span title={helpText} aria-label={helpText} data-testid="lumen-help-link"
            className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center" style={{ border: `1px solid ${C.border}`, color: C.text }}>
            <HelpCircle className="h-5 w-5" />
          </span>
        )}
      </header>

      {showPromo && (
        <div className="text-white text-xs" style={{ backgroundColor: accent }}>
          <div className="max-w-7xl mx-auto px-6 py-2 flex items-center justify-center gap-6 flex-wrap">
            {promoItems.map((it, i) => (
              <span key={i} className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> {it}</span>
            ))}
          </div>
        </div>
      )}

      {/* Mobile collapsible summary */}
      <MobileSummaryBar
        open={summaryOpen} onToggle={() => setSummaryOpen((v) => !v)}
        cartLines={cartLines} lineDiscounts={lineDiscounts} subtotal={subtotal}
        bogoDiscount={bogoDiscount} summaryOfferLabel={summaryOfferLabel}
        selectedShipping={selectedShipping} total={total} accentColor={accent} t={t}
        promoProps={promoProps} promoDiscount={promoDiscount}
      />

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 max-w-7xl mx-auto w-full px-6 md:px-8 pt-8 pb-32 lg:pb-40">
        {/* LEFT: form */}
        <div className="lg:col-span-7 space-y-6 lg:space-y-8 order-1">
          {showSteps && (
            <div className="w-full max-w-md mx-auto flex items-center justify-between" data-testid="lumen-steps">
              {steps.map((s, i) => (
                <div key={s} className="flex items-center flex-1 last:flex-none">
                  <div className="flex flex-col items-center gap-1.5">
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${i === 0 ? 'text-white' : 'bg-zinc-200 text-zinc-500'}`} style={i === 0 ? { backgroundColor: accent } : {}}>{i + 1}</span>
                    <span className={`text-xs font-semibold ${i === 0 ? 'text-zinc-900' : 'text-zinc-400'}`}>{s}</span>
                  </div>
                  {i < steps.length - 1 && <div className="flex-1 h-px bg-zinc-200 mx-2 mb-5" />}
                </div>
              ))}
            </div>
          )}

          {showExpress && psp === 'stripe' && paymentMode === 'element' && stripeInstance && elementsOptions && (
            <div>
              <p className="text-center text-sm text-zinc-500 mb-3">{lt.express}</p>
              <Elements stripe={stripeInstance} options={elementsOptions}>
                <ExpressCheckout
                  amountCents={Math.max(50, Math.round(total * 100))} total={total}
                  items={stripeItems} source={source} selectedShipping={selectedShipping}
                  shippingOptionId={selectedShippingId} accentColor={accent}
                  storeId={pixels.store_id} storeSlug={pixels.store_slug} t={t}
                />
              </Elements>
            </div>
          )}

          <section className={cardCls} style={cardStyle}>
            <SectionHeader icon={User} n={1} title={lt.sec_personal} />
            <CustomerFields form={form} updateField={updateField} setForm={setForm} t={t} section="personal" />
          </section>

          <section className={cardCls} style={cardStyle}>
            <SectionHeader icon={MapPin} n={2} title={lt.sec_address} />
            <CustomerFields form={form} updateField={updateField} setForm={setForm} t={t} section="address" />
            <div className="mt-4">
              <p className="text-xs mb-2 font-medium" style={{ color: C.text }}>{t.shipping}</p>
              <ShippingPicker options={shippingOptions} value={selectedShippingId} onChange={setSelectedShippingId} accentColor={accent} t={t} />
            </div>
          </section>

          <section className={cardCls} style={cardStyle}>
            <SectionHeader icon={CreditCard} n={3} title={lt.sec_payment} />
            {psp === 'payzen' ? (
              <PayzenPayForm
                items={stripeItems} total={total} form={form} hasRequiredCustomerInfo={hasRequiredCustomerInfo} source={source}
                shippingOptionId={selectedShippingId} shippingChosen={!!selectedShipping}
                accentColor={accent} storeId={pixels.store_id} storeSlug={pixels.store_slug}
                currency={currency} payzenCfg={payzenCfg} t={t}
              />
            ) : psp === 'viva' ? (
              vivaCfg ? (
                <VivaPayForm
                  items={stripeItems} total={total} form={form}
                  hasRequiredCustomerInfo={hasRequiredCustomerInfo} source={source}
                  shippingOptionId={selectedShippingId} shippingChosen={!!selectedShipping}
                  accentColor={accent} storeId={pixels.store_id} storeSlug={pixels.store_slug}
                  vivaCfg={vivaCfg} firstProductId={cartLines[0]?.product?.id} currency="eur" embedded={false} t={t}
                />
              ) : (
                <div className="h-32 flex flex-col items-center justify-center text-zinc-400 gap-2" data-testid="checkout-pay-loading-viva"><Loader2 className="h-5 w-5 animate-spin" /><span className="text-xs">{t.loading_payment}</span></div>
              )
            ) : (paymentMode === 'invoice' || paymentMode === 'link') ? (
              <StripeInvoicePay
                mode={paymentMode}
                total={total} items={stripeItems} form={form}
                hasRequiredCustomerInfo={hasRequiredCustomerInfo} source={source}
                shippingOptionId={selectedShippingId} shippingChosen={!!selectedShipping}
                accentColor={accent} storeId={pixels.store_id} storeSlug={pixels.store_slug}
                t={t} payLabel={payLabel} lumen
              />
            ) : stripeInstance && elementsOptions ? (
              <Elements stripe={stripeInstance} options={elementsOptions}>
                <PayForm
                  total={total} amountCents={Math.max(50, Math.round(total * 100))}
                  items={stripeItems} form={form} hasRequiredCustomerInfo={hasRequiredCustomerInfo}
                  source={source} shippingOptionId={selectedShippingId} shippingChosen={!!selectedShipping}
                  accentColor={accent} storeId={pixels.store_id} storeSlug={pixels.store_slug}
                  t={t} payLabel={payLabel} lumen
                />
              </Elements>
            ) : (
              <div className="h-32 flex flex-col items-center justify-center text-zinc-400 gap-2" data-testid="checkout-pay-loading"><Loader2 className="h-5 w-5 animate-spin" /><span className="text-xs">{t.loading_payment}</span></div>
            )}
            <div className="mt-4 flex items-center justify-center gap-2 text-sm" style={{ color: C.text }}>
              <ShieldCheck className="h-4 w-4 shrink-0" style={{ color: C.green }} /> {guaranteeText}
            </div>
          </section>
        </div>

        {/* RIGHT: summary + trust */}
        <div className="lg:col-span-5 order-2">
          <div className="lg:sticky lg:top-24 space-y-5">
            <div className={`${cardCls} hidden lg:block`} style={cardStyle} data-testid={CHECKOUT.productCard}>
              <h2 className="text-lg font-bold mb-4" style={{ color: C.title }}>{lt.order_summary}</h2>
              <div className="space-y-4">
                {cartLines.map((line, idx) => {
                  const displayImage = line.variant?.image || line.product.image_url;
                  const variantLabel = line.variant?.title || '';
                  const lineDisc = lineDiscounts[idx] || 0;
                  const lineNet = lineGross(line) - lineDisc;
                  return (
                    <div key={`${line.product.id}-${idx}`} className="flex gap-3" data-testid={`checkout-cart-line-${idx}`}>
                      {displayImage ? (
                        <img src={displayImage} alt={line.product.name} className="w-16 h-16 object-cover rounded-lg border border-zinc-100" data-testid={`checkout-cart-line-${idx}-image`} />
                      ) : <div className="w-16 h-16 bg-zinc-100 rounded-lg shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm leading-tight">{line.product.name}</div>
                        {variantLabel && <div className="text-xs text-zinc-500 mt-0.5" data-testid={`checkout-cart-line-${idx}-variant`}>{variantLabel}</div>}
                        {line.product.bogo_enabled && lineDisc > 0 && (
                          <div className="inline-flex items-center gap-1 mt-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5" data-testid={`checkout-cart-line-${idx}-bogo`}>{offerTag(line.product)}</div>
                        )}
                        {!isCartMode && cartLines.length === 1 ? (
                          <div className="mt-2 flex items-center gap-1.5">
                            <button type="button" data-testid="checkout-qty-minus" onClick={() => setQuantity(quantity - 1)} className="w-7 h-7 rounded-md border border-zinc-200 hover:bg-zinc-50">−</button>
                            <Input data-testid={CHECKOUT.quantityInput} type="number" value={quantity} min={1} onChange={(e) => setQuantity(parseInt(e.target.value) || 1)} className="w-12 h-7 text-center text-sm rounded-md" />
                            <button type="button" data-testid="checkout-qty-plus" onClick={() => setQuantity(quantity + 1)} className="w-7 h-7 rounded-md border border-zinc-200 hover:bg-zinc-50">+</button>
                          </div>
                        ) : (
                          <div className="mt-1 text-xs text-zinc-500">{t.qty} : <span className="font-mono">{line.quantity}</span></div>
                        )}
                      </div>
                      <div className="text-right text-sm font-semibold shrink-0">
                        {lineDisc > 0 ? (
                          <div data-testid={`checkout-cart-line-${idx}-price`}>
                            <span className="line-through text-zinc-400 font-normal text-xs block">{money(lineGross(line))}</span>
                            <span className="text-emerald-700">{money(lineNet)}</span>
                          </div>
                        ) : <span data-testid={`checkout-cart-line-${idx}-price`}>{money(lineGross(line))}</span>}
                      </div>
                    </div>
                  );
                })}
                {skipped.length > 0 && (
                  <div data-testid="checkout-skipped-warning" className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">⚠️ {skipped.length > 1 ? t.skipped_many.replace('{n}', skipped.length) : t.skipped_one}</div>
                )}
              </div>

              <div className="mt-5"><PromoCodeField {...promoProps} accent={accent} /></div>
              <div className="mt-5 pt-4 border-t border-zinc-100 space-y-2.5 text-sm">
                <div className="flex justify-between"><span className="text-zinc-600">{t.subtotal}</span><span className="font-mono">{money(subtotal)}</span></div>
                {bogoDiscount > 0 && (
                  <div className="flex justify-between text-emerald-700" data-testid="checkout-bogo-discount"><span>{summaryOfferLabel}</span><span className="font-mono">−{money(bogoDiscount)}</span></div>
                )}
                <PromoSummaryRow promo={promo} discount={promoDiscount} lang={promoProps.lang} money={money} />
                <div className="flex justify-between">
                  <span className="text-zinc-600">{selectedShipping?.name || t.shipping}</span>
                  <span className="font-mono">{selectedShipping ? (selectedShipping.price === 0 ? <span className="text-emerald-600 font-semibold">{t.free}</span> : money(selectedShipping.price)) : t.to_choose}</span>
                </div>
                <div className="border-t border-zinc-200 pt-3 flex justify-between items-baseline">
                  <span className="text-lg font-bold" style={{ color: C.title }}>{t.total}</span>
                  <span data-testid={CHECKOUT.totalAmount} className="text-2xl font-extrabold font-mono" style={{ color: C.title }}>{money(total)}</span>
                </div>
                <p className="text-xs text-zinc-400">{lt.tax_included}</p>
              </div>
            </div>

            {/* Trust list */}
            <div className="space-y-2 hidden lg:block">
              {trustItems.map((item, i) => {
                const Ico = lumenIcon(item.icon);
                return (
                  <div key={i} className="flex items-start gap-3 p-3 rounded-lg" style={{ backgroundColor: C.card, border: `1px solid ${C.border}` }}>
                    <Ico className="w-5 h-5 shrink-0 mt-0.5" style={{ color: C.title }} />
                    <div>
                      <span className="text-sm font-semibold block" style={{ color: C.title }}>{item.title}</span>
                      {item.subtitle && <span className="text-xs block" style={{ color: C.text }}>{item.subtitle}</span>}
                    </div>
                  </div>
                );
              })}
            </div>

            {tpEnabled && (
              <div className="flex flex-col items-center justify-center gap-1.5 p-5 bg-[#FDFBF9] border border-zinc-100 rounded-xl" data-testid="lumen-trustpilot-block">
                <span className="text-sm font-bold">{ui.trustpilot_label || lt.tp_excellent}</span>
                <div className="flex gap-0.5">
                  {[0, 1, 2, 3, 4].map((s) => (
                    <span key={s} className="w-6 h-6 bg-[#00B67A] flex items-center justify-center rounded-sm"><Star className="w-4 h-4 text-white fill-white" /></span>
                  ))}
                </div>
                <span className="text-xs text-zinc-500">{ui.trustpilot_rating || '4.8'} / 5{ui.trustpilot_reviews ? ` · ${ui.trustpilot_reviews}` : ''}</span>
                <span className="inline-flex items-center gap-1 text-sm font-semibold text-zinc-800 mt-1"><Star className="w-4 h-4 text-[#00B67A] fill-[#00B67A]" /> Trustpilot</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Benefits footer — in-flow on mobile, sticky on desktop */}
      <footer className="lg:fixed lg:bottom-0 lg:left-0 lg:right-0 z-40 mb-24 lg:mb-0" style={{ backgroundColor: C.card, borderTop: `1px solid ${C.border}` }}>
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-4 grid grid-cols-4 gap-3 lg:gap-6">
          {footerItems.map((item, i) => {
            const Ico = lumenIcon(item.icon);
            return (
              <div key={i} className="flex flex-col items-center text-center gap-1.5 lg:flex-row lg:items-center lg:text-left lg:gap-3">
                <Ico className="w-5 h-5 lg:w-6 lg:h-6 shrink-0" style={{ color: C.title }} />
                <div>
                  <div className="text-[11px] lg:text-sm font-semibold leading-tight" style={{ color: C.title }}>{item.title}</div>
                  {item.subtitle && <div className="text-[10px] lg:text-xs leading-tight" style={{ color: C.text }}>{item.subtitle}</div>}
                </div>
              </div>
            );
          })}
        </div>
      </footer>
    </div>
  );
};


// ---------------------------------------------------------------------------
// SHOPIFY-style checkout template — a faithful, trust-optimized clone of the
// native Shopify Payments checkout (2 columns: form left / order summary right).
// Reuses the SAME shared building blocks (CustomerFields, ShippingPicker,
// PayForm, ExpressCheckout, MobileSummaryBar) so pricing/payment logic never
// diverges. Fully responsive: the summary collapses into MobileSummaryBar.
// ---------------------------------------------------------------------------
const SLABELS = {
  fr: { contact: 'Coordonnées', delivery: 'Livraison', ship_method: "Mode d'expédition", payment: 'Paiement', express: 'Paiement express', or: 'OU', secure_note: 'Toutes les transactions sont sécurisées et chiffrées.', pay_now: 'Payer maintenant', discount_ph: 'Code de réduction', apply: 'Valider', order_summary: 'Récapitulatif', secured: 'Paiement sécurisé', tax_included: 'Taxes incluses', first_name: 'Prénom', last_name: 'Nom', discount_invalid: "Ce code de réduction n'est pas valide." },
  en: { contact: 'Contact', delivery: 'Delivery', ship_method: 'Shipping method', payment: 'Payment', express: 'Express checkout', or: 'OR', secure_note: 'All transactions are secure and encrypted.', pay_now: 'Pay now', discount_ph: 'Discount code', apply: 'Apply', order_summary: 'Order summary', secured: 'Secure payment', tax_included: 'Taxes included', first_name: 'First name', last_name: 'Last name', discount_invalid: 'This discount code is not valid.' },
  es: { contact: 'Contacto', delivery: 'Envío', ship_method: 'Método de envío', payment: 'Pago', express: 'Pago exprés', or: 'O', secure_note: 'Todas las transacciones son seguras y cifradas.', pay_now: 'Pagar ahora', discount_ph: 'Código de descuento', apply: 'Aplicar', order_summary: 'Resumen', secured: 'Pago seguro', tax_included: 'Impuestos incluidos', first_name: 'Nombre', last_name: 'Apellidos', discount_invalid: 'Este código de descuento no es válido.' },
  de: { contact: 'Kontakt', delivery: 'Lieferung', ship_method: 'Versandart', payment: 'Zahlung', express: 'Express-Checkout', or: 'ODER', secure_note: 'Alle Transaktionen sind sicher und verschlüsselt.', pay_now: 'Jetzt bezahlen', discount_ph: 'Rabattcode', apply: 'Anwenden', order_summary: 'Bestellübersicht', secured: 'Sichere Zahlung', tax_included: 'Inkl. Steuern', first_name: 'Vorname', last_name: 'Nachname', discount_invalid: 'Dieser Rabattcode ist ungültig.' },
  it: { contact: 'Contatto', delivery: 'Spedizione', ship_method: 'Metodo di spedizione', payment: 'Pagamento', express: 'Pagamento express', or: 'O', secure_note: 'Tutte le transazioni sono sicure e crittografate.', pay_now: 'Paga ora', discount_ph: 'Codice sconto', apply: 'Applica', order_summary: 'Riepilogo', secured: 'Pagamento sicuro', tax_included: 'Tasse incluse', first_name: 'Nome', last_name: 'Cognome', discount_invalid: 'Questo codice sconto non è valido.' },
  nl: { contact: 'Contact', delivery: 'Levering', ship_method: 'Verzendmethode', payment: 'Betaling', express: 'Express afrekenen', or: 'OF', secure_note: 'Alle transacties zijn veilig en versleuteld.', pay_now: 'Nu betalen', discount_ph: 'Kortingscode', apply: 'Toepassen', order_summary: 'Overzicht', secured: 'Veilige betaling', tax_included: 'Incl. btw', first_name: 'Voornaam', last_name: 'Achternaam', discount_invalid: 'Deze kortingscode is niet geldig.' },
  pl: { contact: 'Kontakt', delivery: 'Dostawa', ship_method: 'Metoda wysyłki', payment: 'Płatność', express: 'Szybka płatność', or: 'LUB', secure_note: 'Wszystkie transakcje są bezpieczne i szyfrowane.', pay_now: 'Zapłać teraz', discount_ph: 'Kod rabatowy', apply: 'Zastosuj', order_summary: 'Podsumowanie', secured: 'Bezpieczna płatność', tax_included: 'Z podatkiem', first_name: 'Imię', last_name: 'Nazwisko', discount_invalid: 'Ten kod rabatowy jest nieprawidłowy.' },
};

const CardBadges = () => (
  <div className="flex items-center gap-1.5" data-testid="shopify-card-badges">
    <span className="h-5 px-1.5 rounded bg-white border border-zinc-200 text-[9px] font-bold tracking-tight flex items-center" style={{ color: '#1A1F71' }}>VISA</span>
    <span className="h-5 w-8 rounded bg-white border border-zinc-200 flex items-center justify-center gap-0"><span className="w-3 h-3 rounded-full" style={{ backgroundColor: '#EB001B' }} /><span className="w-3 h-3 rounded-full -ml-1.5" style={{ backgroundColor: '#F79E1B', opacity: 0.9 }} /></span>
    <span className="h-5 px-1.5 rounded text-white text-[9px] font-bold flex items-center" style={{ backgroundColor: '#2E77BC' }}>AMEX</span>
  </div>
);

const ShopifySection = ({ title, children, testid, num }) => (
  <section data-testid={testid} className="mb-8">
    <h2 className="flex items-center gap-2.5 text-[22px] leading-tight font-bold text-[#1a1a1a] tracking-tight mb-4">
      {num ? <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#1a1a1a] text-white text-[13px] font-semibold shrink-0" style={{ fontFamily: 'Inter, sans-serif' }}>{num}</span> : null}
      {title}
    </h2>
    {children}
  </section>
);


const ShopifyCheckout = ({ vm }) => {
  const {
    branding, t, pixels, cartLines, lineDiscounts, subtotal, bogoDiscount,
    summaryOfferLabel, selectedShipping, total, offerTag, skipped, isCartMode,
    quantity, setQuantity, psp, vivaCfg, payzenCfg, stripeInstance, elementsOptions,
    form, updateField, setForm, hasRequiredCustomerInfo, shippingOptions,
    selectedShippingId, setSelectedShippingId, source, summaryOpen, setSummaryOpen,
    stripeItems, isPreview, geoLang, paymentMode, currency, promo, promoDiscount, promoProps,
  } = vm;

  const lang = resolveLang(branding.language, geoLang);
  const L = SLABELS[lang] || SLABELS.en;
  const ui = branding.checkout_ui || {};
  const accent = branding.accent_color || '#1A1A1A';
  const lineGross = (line) => line.product.price * line.quantity;
  const payLabel = (ui.i18n && ui.i18n[lang] && ui.i18n[lang].pay_button_text) || L.pay_now;
  const showExpress = ui.show_express !== false;

  const OrderSummary = (
    <div data-testid={CHECKOUT.productCard}>
      <div className="space-y-4">
        {cartLines.map((line, idx) => {
          const displayImage = line.variant?.image || line.product.image_url;
          const variantLabel = line.variant?.title || '';
          const lineDisc = lineDiscounts[idx] || 0;
          const lineNet = lineGross(line) - lineDisc;
          return (
            <div key={`${line.product.id}-${idx}`} className="flex gap-3 items-center" data-testid={`checkout-cart-line-${idx}`}>
              <div className="relative shrink-0">
                {displayImage ? (
                  <img src={resolveImageUrl(displayImage)} alt={line.product.name} className="w-14 h-14 object-cover rounded-lg border border-zinc-200" data-testid={`checkout-cart-line-${idx}-image`} />
                ) : <div className="w-14 h-14 bg-zinc-100 rounded-lg border border-zinc-200" />}
                <span className="absolute -top-2 -right-2 min-w-[20px] h-5 px-1 rounded-full bg-[#6B7280] text-white text-[11px] font-semibold flex items-center justify-center">{line.quantity}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm text-[#333] leading-tight">{line.product.name}</div>
                {variantLabel && <div className="text-xs text-zinc-500 mt-0.5" data-testid={`checkout-cart-line-${idx}-variant`}>{variantLabel}</div>}
                {line.product.bogo_enabled && lineDisc > 0 && (
                  <div className="inline-flex items-center gap-1 mt-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5" data-testid={`checkout-cart-line-${idx}-bogo`}>{offerTag(line.product)}</div>
                )}
                {!isCartMode && cartLines.length === 1 && (
                  <div className="mt-1.5 flex items-center gap-1.5">
                    <button type="button" data-testid="checkout-qty-minus" onClick={() => setQuantity(quantity - 1)} className="w-6 h-6 rounded border border-zinc-300 text-zinc-600 hover:bg-white">−</button>
                    <span className="text-xs font-mono w-6 text-center">{quantity}</span>
                    <button type="button" data-testid="checkout-qty-plus" onClick={() => setQuantity(quantity + 1)} className="w-6 h-6 rounded border border-zinc-300 text-zinc-600 hover:bg-white">+</button>
                  </div>
                )}
              </div>
              <div className="text-right text-sm font-medium text-[#333] shrink-0">
                {lineDisc > 0 ? (
                  <div data-testid={`checkout-cart-line-${idx}-price`}>
                    <span className="line-through text-zinc-400 font-normal text-xs block">{money(lineGross(line))}</span>
                    <span className="text-emerald-700">{money(lineNet)}</span>
                  </div>
                ) : <span data-testid={`checkout-cart-line-${idx}-price`}>{money(lineGross(line))}</span>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Discount code (Shopify fidelity) */}
      <div className="mt-5"><PromoCodeField {...promoProps} accent={accent} /></div>

      <div className="mt-5 pt-4 border-t border-zinc-200 space-y-2.5 text-sm">
        <div className="flex justify-between"><span className="text-[#555]">{t.subtotal}</span><span className="font-medium text-[#333]">{money(subtotal)}</span></div>
        {bogoDiscount > 0 && (
          <div className="flex justify-between text-emerald-700" data-testid="checkout-bogo-discount"><span>{summaryOfferLabel}</span><span className="font-medium">−{money(bogoDiscount)}</span></div>
        )}
        <PromoSummaryRow promo={promo} discount={promoDiscount} lang={promoProps.lang} money={money} />
        <div className="flex justify-between">
          <span className="text-[#555]">{selectedShipping?.name || t.shipping}</span>
          <span className="font-medium text-[#333]">{selectedShipping ? (selectedShipping.price === 0 ? <span className="text-emerald-600 font-semibold">{t.free}</span> : money(selectedShipping.price)) : t.to_choose}</span>
        </div>
        <div className="border-t border-zinc-200 pt-3 flex justify-between items-baseline">
          <span className="text-base font-semibold text-[#1a1a1a]">{t.total}</span>
          <span data-testid={CHECKOUT.totalAmount} className="text-[24px] font-bold text-[#1a1a1a]"><span className="text-xs font-medium text-zinc-400 mr-1.5 uppercase">EUR</span>{money(total)}</span>
        </div>
        <p className="text-xs text-zinc-400">{L.tax_included}</p>
      </div>
      {skipped.length > 0 && (
        <div data-testid="checkout-skipped-warning" className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">⚠️ {skipped.length > 1 ? t.skipped_many.replace('{n}', skipped.length) : t.skipped_one}</div>
      )}
      <TrustpilotBlock ui={ui} className="mt-6" />
    </div>
  );

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', color: '#1a1a1a' }} data-testid="shopify-checkout-form">
      {pixels.store_id && !isPreview && (
        <PixelTracker storeId={pixels.store_id} slug={pixels.store_slug} event="page_view_checkout" metaPixelId={pixels.meta_pixel_id} tiktokPixelId={pixels.tiktok_pixel_id} googleTagId={pixels.google_tag_id} googleLabel={pixels.google_conversion_label} />
      )}

      {/* Header — store brand left, secured badges + cart (Shopify style) */}
      <header className="border-b border-zinc-200">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 h-[68px] flex items-center justify-between gap-3" data-testid="shopify-store-brand">
          <div className="min-w-0 flex items-center">
            {branding.logo_url ? (
              <img src={resolveImageUrl(branding.logo_url)} alt={branding.display_name} className="h-9 max-w-[180px] object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            ) : (
              <span className="text-[22px] font-bold tracking-tight text-[#1a1a1a] truncate">{branding.display_name || t.store_fallback}</span>
            )}
          </div>
          <div className="flex items-center gap-4 shrink-0">
            <div className="hidden sm:block"><CardBadges /></div>
            <ShoppingBag className="h-6 w-6 text-[#1a1a1a]" strokeWidth={1.6} />
          </div>
        </div>
      </header>

      {/* Mobile collapsible summary bar */}
      <MobileSummaryBar
        open={summaryOpen} onToggle={() => setSummaryOpen((v) => !v)}
        cartLines={cartLines} lineDiscounts={lineDiscounts} subtotal={subtotal}
        bogoDiscount={bogoDiscount} summaryOfferLabel={summaryOfferLabel}
        selectedShipping={selectedShipping} total={total} accentColor={accent} t={t}
        promoProps={promoProps} promoDiscount={promoDiscount}
      />

      <div className="max-w-[1100px] mx-auto lg:grid lg:grid-cols-[1fr_minmax(0,440px)]">
        {/* LEFT: form */}
        <div className="px-4 sm:px-6 lg:pr-12 py-6 lg:py-10 order-1">
          {showExpress && psp === 'stripe' && paymentMode === 'element' && stripeInstance && elementsOptions && (
            <div className="mb-7">
              <Elements stripe={stripeInstance} options={elementsOptions}>
                <ExpressCheckout
                  amountCents={Math.max(50, Math.round(total * 100))} total={total}
                  items={stripeItems} source={source} selectedShipping={selectedShipping}
                  shippingOptionId={selectedShippingId} accentColor={accent}
                  storeId={pixels.store_id} storeSlug={pixels.store_slug} t={t}
                />
              </Elements>
              <div className="flex items-center gap-3 mt-5">
                <span className="flex-1 h-px bg-zinc-200" />
                <span className="text-xs text-zinc-400">{L.or}</span>
                <span className="flex-1 h-px bg-zinc-200" />
              </div>
            </div>
          )}

          <ShopifySection title={L.contact} testid="shopify-sec-contact" num={1}>
            <div>
              <Label htmlFor="sh-email" className="text-xs text-zinc-500">{t.email}</Label>
              <Input id="sh-email" type="email" autoComplete="email" data-testid={CHECKOUT.emailInput}
                value={form.email} onChange={(e) => updateField('email', e.target.value)}
                className="mt-1 h-12 rounded-md border-zinc-300 text-base" placeholder={t.email_ph} required />
            </div>
          </ShopifySection>

          <ShopifySection title={L.delivery} testid="shopify-sec-delivery" num={2}>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <Label htmlFor="sh-fn" className="text-xs text-zinc-500">{L.first_name}</Label>
                <Input id="sh-fn" autoComplete="given-name" data-testid={CHECKOUT.firstNameInput}
                  value={form.first_name} onChange={(e) => updateField('first_name', e.target.value)}
                  className="mt-1 h-12 rounded-md border-zinc-300 text-base" required />
              </div>
              <div>
                <Label htmlFor="sh-ln" className="text-xs text-zinc-500">{L.last_name}</Label>
                <Input id="sh-ln" autoComplete="family-name" data-testid={CHECKOUT.lastNameInput}
                  value={form.last_name} onChange={(e) => updateField('last_name', e.target.value)}
                  className="mt-1 h-12 rounded-md border-zinc-300 text-base" required />
              </div>
            </div>
            <CustomerFields form={form} updateField={updateField} setForm={setForm} t={t} section="address" shopify />
          </ShopifySection>

          <ShopifySection title={L.ship_method} testid="shopify-sec-shipping" num={3}>
            <ShippingPicker options={shippingOptions} value={selectedShippingId} onChange={setSelectedShippingId} accentColor={accent} t={t} />
          </ShopifySection>

          <ShopifySection title={L.payment} testid="shopify-sec-payment" num={4}>
            <p className="text-xs text-zinc-500 mb-3 flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" /> {L.secure_note}</p>
            <div className="rounded-lg border border-zinc-300 overflow-hidden">
              <div className="p-4">
                {psp === 'payzen' ? (
                  <PayzenPayForm
                    items={stripeItems} total={total} form={form} hasRequiredCustomerInfo={hasRequiredCustomerInfo} source={source}
                    shippingOptionId={selectedShippingId} shippingChosen={!!selectedShipping}
                    accentColor={accent} storeId={pixels.store_id} storeSlug={pixels.store_slug}
                    currency={currency} payzenCfg={payzenCfg} t={t}
                  />
                ) : psp === 'viva' ? (
                  vivaCfg ? (
                    <VivaPayForm
                      items={stripeItems} total={total} form={form}
                      hasRequiredCustomerInfo={hasRequiredCustomerInfo} source={source}
                      shippingOptionId={selectedShippingId} shippingChosen={!!selectedShipping}
                      accentColor={accent} storeId={pixels.store_id} storeSlug={pixels.store_slug}
                      vivaCfg={vivaCfg} firstProductId={cartLines[0]?.product?.id} currency="eur" embedded={false} t={t}
                    />
                  ) : (
                    <div className="h-32 flex flex-col items-center justify-center text-zinc-400 gap-2" data-testid="checkout-pay-loading-viva"><Loader2 className="h-5 w-5 animate-spin" /><span className="text-xs">{t.loading_payment}</span></div>
                  )
                ) : (paymentMode === 'invoice' || paymentMode === 'link') ? (
                  <StripeInvoicePay
                    mode={paymentMode}
                    total={total} items={stripeItems} form={form}
                    hasRequiredCustomerInfo={hasRequiredCustomerInfo} source={source}
                    shippingOptionId={selectedShippingId} shippingChosen={!!selectedShipping}
                    accentColor={accent} storeId={pixels.store_id} storeSlug={pixels.store_slug}
                    t={t} payLabel={payLabel}
                  />
                ) : stripeInstance && elementsOptions ? (
                  <Elements stripe={stripeInstance} options={elementsOptions}>
                    <PayForm
                      total={total} amountCents={Math.max(50, Math.round(total * 100))}
                      items={stripeItems} form={form} hasRequiredCustomerInfo={hasRequiredCustomerInfo}
                      source={source} shippingOptionId={selectedShippingId} shippingChosen={!!selectedShipping}
                      accentColor={accent} storeId={pixels.store_id} storeSlug={pixels.store_slug}
                      t={t} payLabel={payLabel} shopify
                    />
                  </Elements>
                ) : (
                  <div className="h-32 flex flex-col items-center justify-center text-zinc-400 gap-2" data-testid="checkout-pay-loading"><Loader2 className="h-5 w-5 animate-spin" /><span className="text-xs">{t.loading_payment}</span></div>
                )}
              </div>
            </div>
          </ShopifySection>

          {/* Trust footer — discrete, Shopify-like (no Shopify branding) */}
          <div className="mt-8 pt-6 border-t border-zinc-200 flex flex-col sm:flex-row items-center justify-between gap-3" data-testid="shopify-trust-footer">
            <span className="text-xs text-zinc-500 flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" /> {L.secured}</span>
            <CardBadges />
          </div>

          {/* Trustpilot — mobile only (desktop shows it in the right summary panel) */}
          <TrustpilotBlock ui={ui} className="lg:hidden mt-6" testId="trustpilot-block-mobile" />
        </div>

        {/* RIGHT: order summary (gray panel), desktop only; mobile uses the bar */}
        <div className="hidden lg:block bg-[#fafafa] border-l border-zinc-200 order-2">
          <div className="lg:sticky lg:top-0 px-8 py-10">
            {OrderSummary}
          </div>
        </div>
      </div>
    </div>
  );
};
